import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowDownWideNarrow,
  ArrowUpRight,
  CheckCircle2,
  ChevronDown,
  Circle,
  Clock3,
  ClipboardList,
  Copy,
  CreditCard,
  FileText,
  FileUp,
  FolderKanban,
  LayoutDashboard,
  LockKeyhole,
  LogOut,
  MessageSquare,
  Plus,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  UploadCloud,
  UserCircle
} from 'lucide-react';
import {
  addRequestComment,
  createBillingRecord,
  createChangeRequest,
  createClient,
  createProject,
  createRevenueCompanies,
  createSite,
  convertBottleneckLead,
  generateRevenueAudit,
  getBottleneckLead,
  getBottleneckLeads,
  getChetzelsReviewSession,
  getChetzelsReviewSessions,
  getMe,
  getOverview,
  getRequest,
  getRevenueAudit,
  getRevenueCompanies,
  getRevenueDiscovery,
  inviteUser,
  logout,
  onboardClient,
  openStripePortal,
  requestMagicLink,
  updateBottleneckLead,
  updateClientServiceLevel,
  updateRevenueCompany,
  uploadClientFile
} from './lib/api.js';
import {
  buildWireframeChangeBrief,
  filterReviewSessions,
  getReviewChat,
  getReviewNotes,
  summarizeReviewSessions
} from './lib/chetzelsReview.js';
import { demoOverview, demoSession } from './lib/demoData.js';
import { commentsForRequest, isOwnComment } from './lib/requestThread.js';
import {
  filterRevenueCompaniesByQueue,
  filterRevenueCompaniesByPipeline,
  filterRevenueCompanies,
  getRevenueCommandSummary,
  getRevenueNextAction,
  getGradeTone,
  sortRevenueCompanies,
  summarizeRevenueCompanies
} from './lib/revenueView.js';

const navItems = [
  { id: 'dashboard', label: 'Home', icon: LayoutDashboard },
  { id: 'projects', label: 'Projects', icon: FolderKanban },
  { id: 'requests', label: 'Requests', icon: FileText },
  { id: 'files', label: 'Files', icon: FileUp },
  { id: 'billing', label: 'Billing', icon: CreditCard }
];

const viewTitles = {
  dashboard: 'Home',
  projects: 'Projects',
  requests: 'Requests',
  files: 'Files',
  billing: 'Billing',
  account: 'Account',
  bottlenecks: 'Bottlenecks',
  reviews: 'Chetzels Reviews',
  revenue: 'Revenue',
  admin: 'Admin'
};

const adminNav = { id: 'admin', label: 'Admin', icon: ShieldCheck };
const bottleneckNav = { id: 'bottlenecks', label: 'Bottlenecks', icon: ClipboardList };
const reviewsNav = { id: 'reviews', label: 'Reviews', icon: MessageSquare };
const revenueNav = { id: 'revenue', label: 'Revenue', icon: Sparkles };
const requestTypes = ['content', 'design', 'bug', 'seo', 'integration', 'billing', 'support', 'other'];
const priorities = ['low', 'normal', 'high', 'urgent'];
const serviceStatuses = ['active', 'paused', 'canceled'];
const servicePackageOptions = [
  { key: 'foundation_care', name: 'Foundation Care' },
  { key: 'momentum_care', name: 'Momentum Care' },
  { key: 'signature_growth', name: 'Signature Growth' }
];
const ownershipLabels = {
  client_owned: 'Client-owned',
  jje_managed: 'JJE-managed',
  hybrid: 'Hybrid'
};

function isDemoMode() {
  const params = new URLSearchParams(window.location.search);
  return params.has('demo') && ['localhost', '127.0.0.1'].includes(window.location.hostname);
}

export default function App() {
  const [session, setSession] = useState(null);
  const [overview, setOverview] = useState(null);
  const [view, setView] = useState(new URLSearchParams(window.location.search).get('view') || 'dashboard');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const demo = isDemoMode();

  function navigate(next) {
    setView(next);
    const params = new URLSearchParams(window.location.search);
    params.set('view', next);
    window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
  }

  useEffect(() => {
    function handlePopState() {
      setView(new URLSearchParams(window.location.search).get('view') || 'dashboard');
    }
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function load() {
      setLoading(true);
      setError('');
      try {
        if (demo) {
          setSession(demoSession);
          setSelectedClientId(demoSession.defaultClientId);
          setOverview(demoOverview);
          return;
        }

        const me = await getMe();
        if (!isMounted) return;
        setSession(me);
        setSelectedClientId(me.defaultClientId || me.memberships?.[0]?.clientId || '');
      } catch (requestError) {
        if (isMounted) setSession(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [demo]);

  useEffect(() => {
    let isMounted = true;
    async function loadOverview() {
      if (!session || !selectedClientId) return;
      setError('');
      try {
        if (demo) {
          setOverview({
            ...demoOverview,
            client: demoOverview.clients.find((client) => client.id === selectedClientId) || demoOverview.client
          });
          return;
        }
        const data = await getOverview(selectedClientId);
        if (isMounted) setOverview(data);
      } catch (requestError) {
        if (isMounted) setError(requestError.message);
      }
    }

    loadOverview();
    return () => {
      isMounted = false;
    };
  }, [session, selectedClientId, demo]);

  async function refresh(message = '') {
    if (!session || demo) {
      if (message) setNotice(message);
      return;
    }
    const data = await getOverview(selectedClientId);
    setOverview(data);
    if (message) setNotice(message);
  }

  async function handleLogout() {
    if (!demo) await logout();
    setSession(null);
    setOverview(null);
    setNotice('');
  }

  if (loading) {
    return <LoadingScreen />;
  }

  if (!session) {
    return <LoginPage failed={new URLSearchParams(window.location.search).get('login') === 'failed'} />;
  }

  return (
    <PortalShell
      session={session}
      overview={overview}
      view={view}
      setView={navigate}
      selectedClientId={selectedClientId}
      setSelectedClientId={setSelectedClientId}
      notice={notice}
      error={error}
      setNotice={setNotice}
      setError={setError}
      refresh={refresh}
      onLogout={handleLogout}
      demo={demo}
    />
  );
}

function LoadingScreen() {
  return (
    <div className="loading-screen">
      <div className="brand-lockup">
        <span className="brand-mark">JJE</span>
        <span>Creative Client Portal</span>
      </div>
      <div className="loading-line" />
    </div>
  );
}

function LoginPage({ failed }) {
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState(failed ? 'That login link is expired or has already been used.' : '');
  const [submitting, setSubmitting] = useState(false);
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!siteKey) return;
    window.jjeTurnstileCallback = (token) => setTurnstileToken(token);
    if (!document.querySelector('script[data-turnstile]')) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.dataset.turnstile = 'true';
      document.head.appendChild(script);
    }
  }, [siteKey]);

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setStatus('');
    try {
      const result = await requestMagicLink(email, turnstileToken);
      setStatus(result.message);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-shell">
      <section className="login-panel" aria-label="Client portal login">
        <div className="login-brand">
          <span className="brand-mark">JJE</span>
          <div>
            <p>JJE Creative</p>
            <h1>Client Portal</h1>
          </div>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <label htmlFor="email">Email address</label>
          <div className="input-row">
            <LockKeyhole aria-hidden="true" size={18} />
            <input
              id="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@company.com"
              autoComplete="email"
              required
            />
          </div>

          {siteKey ? (
            <div
              className="cf-turnstile"
              data-sitekey={siteKey}
              data-callback="jjeTurnstileCallback"
            />
          ) : null}

          <button className="btn primary full" type="submit" disabled={submitting}>
            {submitting ? <RefreshCw className="spin" size={17} /> : <Sparkles size={17} />}
            <span>{submitting ? 'Sending link' : 'Send Login Link'}</span>
          </button>
        </form>

        {status ? <p className="notice success">{status}</p> : null}
        {error ? <p className="notice danger">{error}</p> : null}
        <p className="login-footnote">Invite-only access for active JJE Creative clients.</p>
      </section>

      <aside className="login-aside">
        <div className="security-card">
          <ShieldCheck aria-hidden="true" />
          <h2>Secure client operations</h2>
          <p>Requests, files, project status, and Stripe-hosted billing stay tied to the right client workspace.</p>
        </div>
      </aside>
    </main>
  );
}

function PortalShell({
  session,
  overview,
  view,
  setView,
  selectedClientId,
  setSelectedClientId,
  notice,
  error,
  setNotice,
  setError,
  refresh,
  onLogout,
  demo
}) {
  const allNav = session.isAdmin ? [...navItems, bottleneckNav, reviewsNav, revenueNav, adminNav] : navItems;
  const client = overview?.client;
  const serviceLevel = overview?.serviceLevel;

  return (
    <div className={`app-shell ${view === 'revenue' ? 'revenue-active-shell' : ''}`}>
      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="brand-mark">JJE</span>
          <div>
            <strong>Creative</strong>
            <span>Client Portal</span>
          </div>
        </div>

        <nav className="sidebar-nav" aria-label="Portal navigation">
          {allNav.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                className={view === item.id ? 'active' : ''}
                onClick={() => setView(item.id)}
                type="button"
              >
                <Icon aria-hidden="true" size={18} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="user-chip">
            <span>{session.user.avatarInitials || getInitials(session.user.name, session.user.email)}</span>
            <div>
              <strong>{session.user.name}</strong>
              <small>{session.isAdmin ? 'JJE staff' : 'Client user'}</small>
            </div>
          </div>
          <button className="icon-text ghost" onClick={onLogout} type="button">
            <LogOut size={17} />
            <span>Log out</span>
          </button>
          <button className={`icon-text ghost ${view === 'account' ? 'active' : ''}`} onClick={() => setView('account')} type="button">
            <UserCircle size={17} />
            <span>Account</span>
          </button>
        </div>
      </aside>

      <main className="workspace">
        <header className="topbar">
          <div>
            <p className="section-kicker">{client?.name || 'Client workspace'}</p>
            <h1>{viewTitles[view] || 'Home'}</h1>
          </div>
          <div className="topbar-actions">
            {serviceLevel ? <ServiceStatusBadge serviceLevel={serviceLevel} /> : null}
            {session.isAdmin && overview?.clients?.length ? (
              <select
                className="client-select"
                value={selectedClientId}
                onChange={(event) => setSelectedClientId(event.target.value)}
                aria-label="Active client"
              >
                {overview.clients.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            ) : null}
            <button className="icon-btn" onClick={() => refresh('Workspace refreshed.')} title="Refresh" aria-label="Refresh" type="button">
              <RefreshCw size={18} aria-hidden="true" />
            </button>
          </div>
        </header>

        {demo ? <p className="notice soft">Local demo data is active.</p> : null}
        {notice ? <DismissibleNotice tone="success" message={notice} onDismiss={() => setNotice('')} /> : null}
        {error ? <DismissibleNotice tone="danger" message={error} onDismiss={() => setError('')} /> : null}

        {!overview ? (
          <LoadingScreen />
        ) : (
          <PortalView
            view={view}
            session={session}
            overview={overview}
            selectedClientId={selectedClientId}
            setSelectedClientId={setSelectedClientId}
            setView={setView}
            refresh={refresh}
            setNotice={setNotice}
            setError={setError}
            demo={demo}
          />
        )}
      </main>
    </div>
  );
}

function PortalView({ view, session, overview, selectedClientId, setSelectedClientId, setView, refresh, setNotice, setError, demo }) {
  if (view === 'projects') return <ProjectsView overview={overview} />;
  if (view === 'requests') return <RequestsView overview={overview} session={session} selectedClientId={selectedClientId} refresh={refresh} setError={setError} demo={demo} />;
  if (view === 'files') return <FilesView overview={overview} selectedClientId={selectedClientId} refresh={refresh} setError={setError} demo={demo} />;
  if (view === 'billing') return <BillingView overview={overview} selectedClientId={selectedClientId} setNotice={setNotice} setError={setError} demo={demo} />;
  if (view === 'account') return <AccountView session={session} overview={overview} />;
  if (view === 'bottlenecks' && session.isAdmin) {
    return <BottlenecksView setSelectedClientId={setSelectedClientId} setNotice={setNotice} setError={setError} demo={demo} />;
  }
  if (view === 'reviews' && session.isAdmin) {
    return <ChetzelsReviewsView setNotice={setNotice} setError={setError} demo={demo} />;
  }
  if (view === 'revenue' && session.isAdmin) {
    return <RevenueView setNotice={setNotice} setError={setError} demo={demo} />;
  }
  if (view === 'admin' && session.isAdmin) {
    return <AdminView overview={overview} selectedClientId={selectedClientId} setSelectedClientId={setSelectedClientId} refresh={refresh} setNotice={setNotice} setError={setError} demo={demo} />;
  }
  return <DashboardView overview={overview} selectedClientId={selectedClientId} setView={setView} setNotice={setNotice} setError={setError} demo={demo} />;
}

function DashboardView({ overview, selectedClientId, setView, setNotice, setError, demo }) {
  const stats = useMemo(() => getStats(overview), [overview]);
  const activeRequests = overview.requests.filter((request) => !['completed', 'canceled'].includes(request.status)).slice(0, 5);
  const activeProject = overview.projects[0];
  const serviceLevel = overview.serviceLevel;

  return (
    <div className="view-grid">
      {serviceLevel ? (
        <section className="panel service-plan-panel">
          <ServicePlanCard serviceLevel={serviceLevel} activeRequests={activeRequests.length} activeProject={activeProject} />
        </section>
      ) : null}

      <section className="metric-row" aria-label="Workspace summary">
        <Metric label="Open requests" value={stats.openRequests} icon={FileText} />
        <Metric label="Active projects" value={stats.activeProjects} icon={FolderKanban} />
        <Metric label="Files" value={stats.files} icon={FileUp} />
        <Metric label="Open invoices" value={stats.billingOpen} icon={CreditCard} />
      </section>

      <section className="panel wide">
        <PanelHead title="Project Pulse" action={activeProject?.phase || 'No active project'} />
        {activeProject ? (
          <div className="project-pulse">
            <div>
              <h2>{activeProject.name}</h2>
              <p>{activeProject.summary || 'Project details are ready for the next update.'}</p>
              <div className="progress-track" aria-label={`${activeProject.progress}% complete`}>
                <span style={{ width: `${activeProject.progress || 0}%` }} />
              </div>
            </div>
            <dl className="pulse-facts">
              <div>
                <dt>Next milestone</dt>
                <dd>{activeProject.nextMilestone || 'Queued with JJE'}</dd>
              </div>
              <div>
                <dt>Target date</dt>
                <dd>{formatDate(activeProject.dueDate)}</dd>
              </div>
            </dl>
          </div>
        ) : (
          <EmptyState title="No project yet" message="Your first project will appear here once we kick things off." />
        )}
      </section>

      <section className="panel">
        <PanelHead title="Requests" action={`${activeRequests.length} active`} />
        <div className="stack">
          {activeRequests.length ? activeRequests.map((request) => (
            <RequestRow key={request.id} request={request} />
          )) : <EmptyState title="No open requests" message="Need a change to your site? Send us a request." action={{ label: 'Request a change', onClick: () => setView('requests') }} />}
        </div>
      </section>

      <section className="panel">
        <PanelHead title="Billing" action="Secure payments" />
        <BillingSummary
          overview={overview}
          selectedClientId={selectedClientId}
          setNotice={setNotice}
          setError={setError}
          demo={demo}
        />
      </section>

      <GettingStarted overview={overview} setView={setView} />

      <section className="panel">
        <PanelHead title="Recent updates from JJE" action={`${overview.comments.length} messages`} />
        <div className="stack">
          {overview.comments.length ? overview.comments.slice(0, 4).map((comment) => (
            <CommentBubble key={comment.id} comment={comment} />
          )) : <EmptyState title="No recent updates" message="Replies and updates from JJE will show up here." />}
        </div>
      </section>
    </div>
  );
}

function GettingStarted({ overview, setView }) {
  const steps = [
    { label: 'Submit your first request', done: overview.requests.length > 0, view: 'requests' },
    { label: 'Upload your brand files', done: overview.files.length > 0, view: 'files' },
    { label: 'Review your plan & billing', done: overview.billing.length > 0, view: 'billing' }
  ];
  const completed = steps.filter((step) => step.done).length;
  if (completed === steps.length) return null;

  return (
    <section className="panel wide">
      <PanelHead title="Getting started" action={`${completed}/${steps.length} done`} />
      <div className="checklist">
        {steps.map((step) => (
          <button
            key={step.label}
            type="button"
            className={step.done ? 'check-item done' : 'check-item action'}
            onClick={() => setView(step.view)}
          >
            {step.done ? <CheckCircle2 size={18} aria-hidden="true" /> : <Circle size={18} aria-hidden="true" />}
            <span>{step.label}</span>
            {step.done
              ? <span className="sr-only">Complete</span>
              : <ArrowUpRight size={16} aria-hidden="true" style={{ marginLeft: 'auto' }} />}
          </button>
        ))}
      </div>
    </section>
  );
}

function ServiceStatusBadge({ serviceLevel }) {
  return (
    <div className={`service-status-badge ${serviceLevel.status || 'active'}`}>
      <span>{serviceLevel.shortName || serviceLevel.name}</span>
      <strong>{titleize(serviceLevel.status || 'active')}</strong>
    </div>
  );
}

function ServicePlanCard({ serviceLevel, activeRequests, activeProject }) {
  const entitlements = serviceLevel.entitlements || {};
  const features = [
    { label: 'Response target', value: serviceLevel.responseTarget || 'Standard queue' },
    { label: 'Request capacity', value: entitlements.requestLimitLabel || `${serviceLevel.monthlyRequestAllowance || 0} requests / month` },
    { label: 'Planning cadence', value: entitlements.planningCadence || 'As needed' },
    { label: 'Analytics', value: serviceLevel.includesAnalytics ? 'Included' : 'Available as add-on' },
    { label: 'Automation', value: serviceLevel.includesAutomation ? 'Included' : 'Scoped separately' }
  ];
  const nextAction = activeRequests
    ? 'Review open requests'
    : activeProject
      ? 'Check the current milestone'
      : 'Start a new request';

  return (
    <div className="service-plan">
      <div className="service-plan-main">
        <div className="service-plan-title">
          <span>Current service level</span>
          <h2>{serviceLevel.name || 'Foundation Care'}</h2>
          <StatusPill status={serviceLevel.status || 'active'} />
        </div>
        <p>{serviceLevel.summary || 'Core portal access and website care support.'}</p>
        <div className="service-next-action">
          <span>Next best action</span>
          <strong>{nextAction}</strong>
        </div>
      </div>
      <div className="service-feature-grid">
        {features.map((feature) => (
          <div key={feature.label}>
            <span>{feature.label}</span>
            <strong>{feature.value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

const bottleneckStatuses = ['all', 'new', 'auto_email_sent', 'manual_review', 'nurture', 'booked_call', 'meeting_scheduled', 'audit_proposed', 'sprint_proposed', 'converted', 'lost', 'do_not_contact', 'unsubscribed'];
const bottleneckFitTiers = ['all', 'Hot / Sales Ready', 'Warm / Audit Fit', 'Nurture / Education', 'Unqualified'];

const demoBottleneckLead = {
  id: 'demo-bot-1',
  firstName: 'Jordan',
  lastName: 'Smith',
  company: 'Smith Plumbing',
  title: 'Owner',
  email: 'jordan@example.com',
  phone: '615-555-0100',
  website: 'https://smithplumbing.example',
  location: 'Franklin, TN',
  industry: 'Home Services',
  emailConsent: true,
  smsConsent: false,
  companySize: '6-10',
  businessDescription: 'Residential plumbing and emergency water heater service.',
  triggerReason: 'Leads pile up when the office is busy.',
  primaryBottleneck: ['Lead follow-up'],
  leadProcess: 'Website leads go to email and someone copies them into a spreadsheet.',
  leadLeakage: 'Weekly',
  timeLostWeek: '11-20',
  repeatTasks: ['Follow up with leads', 'Send reminders', 'Update spreadsheets'],
  currentTools: ['Email', 'Website forms', 'Spreadsheets'],
  toolConnectivity: 'Mostly manual',
  avgCustomerValue: '$1k-$5k',
  monthlyValue: '$3k-$10k',
  urgency: 'ASAP',
  decisionMaker: 'Yes',
  budgetFit: '$5,000-$12,000',
  successOutcome: 'Respond to every emergency lead within five minutes.',
  wantsCall: 'Yes',
  preferredFollowup: 'Email',
  bestTime: 'Weekday mornings',
  aiFollowupQuestion: 'Where do most new leads come from?',
  aiFollowupAnswer: 'Website form',
  flowVersion: 'bottleneck_finder_v2',
  sourcePage: '/bottleneck-review/',
  campaign: 'demo',
  totalScore: 34,
  scoreBreakdown: { time: 6, leadLeakage: 4, toolConnectivity: 3, urgency: 5, decision: 3, budget: 6, customerValue: 2 },
  fitTier: 'Hot / Sales Ready',
  recommendedOffer: 'Automation Sprint',
  followupSequence: 'Hot Lead',
  status: 'new',
  nextAction: 'Personal follow-up within 24 hours.',
  notes: '',
  nextFollowupAt: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
  publicSummary: {
    headline: 'Your clearest bottleneck is lead follow-up.',
    diagnosis: 'Smith Plumbing appears to have a practical opportunity around lead follow-up, especially when office work interrupts new emergency inquiries.',
    quickWins: ['Send an instant confirmation after every inquiry.', 'Create one staff task when a new lead arrives.', 'Add day 2 and day 5 follow-up if no call is booked.'],
    recommendedNextStep: 'The best next step is a 20-minute review call to confirm the workflow and decide whether an Automation Sprint is the right first move.'
  },
  internalSummary: {
    salesReadiness: 'Hot / Sales Ready',
    recommendedAngle: 'Lead response speed and missed emergency jobs.',
    callPrepNotes: ['Ask where web forms land today.', 'Confirm who owns first response.', 'Map spreadsheet and reminder steps.'],
    riskFlags: []
  },
  aiStatus: 'fallback',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

const demoBottleneckData = {
  leads: [
    demoBottleneckLead,
    {
      ...demoBottleneckLead,
      id: 'demo-bot-2',
      firstName: 'Avery',
      lastName: 'Cole',
      company: 'Cole Advisory',
      email: 'avery@example.com',
      industry: 'Professional Services',
      primaryBottleneck: ['Client onboarding'],
      fitTier: 'Warm / Audit Fit',
      recommendedOffer: 'Client Onboarding Automation',
      totalScore: 20,
      status: 'nurture',
      nextAction: 'Invite to AI Operations Audit discussion.',
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
    }
  ],
  metrics: { total: 2, hot: 1, warm: 1, nurture: 0, booked: 0, converted: 0, dueToday: 0 }
};

const demoReviewSession = {
  id: 'review_demo_karina',
  projectKey: 'chetzels-wireframe',
  visitorLabel: 'Karina',
  pageUrl: 'https://claude-wizardly-davinci-77c6.chetzels-bakehouse-webgl.pages.dev/wireframe',
  status: 'active',
  eventCount: 5,
  firstSeenAt: new Date(Date.now() - 90 * 60 * 1000).toISOString(),
  lastSeenAt: new Date().toISOString(),
  userAgent: 'Demo browser'
};

const demoReviewEvents = [
  {
    id: 'review_evt_1',
    eventType: 'note_saved',
    role: 'user',
    section: 'Hero',
    message: 'Make the first photo feel more high-end and food-focused.',
    metadata: { path: '/wireframe#hero' },
    createdAt: new Date(Date.now() - 80 * 60 * 1000).toISOString()
  },
  {
    id: 'review_evt_2',
    eventType: 'chat_message',
    role: 'user',
    section: 'Food gallery',
    message: 'Can we show more close-up food texture before the story?',
    createdAt: new Date(Date.now() - 70 * 60 * 1000).toISOString()
  },
  {
    id: 'review_evt_3',
    eventType: 'chat_message',
    role: 'assistant',
    section: 'Food gallery',
    message: 'Yes. That sounds like a concrete layout change: move more close-up food photography higher and let the story come after customers have seen the menu.',
    createdAt: new Date(Date.now() - 69 * 60 * 1000).toISOString()
  },
  {
    id: 'review_evt_4',
    eventType: 'note_saved',
    role: 'user',
    section: 'Menu links',
    message: 'Keep the PDF menu easy to update, but still show food photos on the page.',
    metadata: { path: '/wireframe#menu' },
    createdAt: new Date(Date.now() - 45 * 60 * 1000).toISOString()
  },
  {
    id: 'review_evt_5',
    eventType: 'brief_copied',
    role: 'user',
    section: 'Overall page',
    message: 'Karina copied the current review notes.',
    createdAt: new Date(Date.now() - 15 * 60 * 1000).toISOString()
  }
];

function ChetzelsReviewsView({ setNotice, setError, demo }) {
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  const visibleSessions = useMemo(() => filterReviewSessions(sessions, searchQuery), [sessions, searchQuery]);
  const selectedSession = detail?.session || visibleSessions.find((session) => session.id === selectedId) || visibleSessions[0] || null;
  const events = detail?.events || [];
  const metrics = useMemo(() => summarizeReviewSessions(sessions, events), [sessions, events]);

  useEffect(() => {
    let isMounted = true;
    async function loadSessions() {
      setLoading(true);
      try {
        const data = demo ? { sessions: [demoReviewSession] } : await getChetzelsReviewSessions({ limit: 100 });
        if (!isMounted) return;
        const nextSessions = data.sessions || [];
        setSessions(nextSessions);
        setSelectedId((current) => {
          if (current && nextSessions.some((session) => session.id === current)) return current;
          return nextSessions[0]?.id || '';
        });
      } catch (requestError) {
        if (isMounted) setError(requestError.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    loadSessions();
    return () => {
      isMounted = false;
    };
  }, [demo, setError]);

  useEffect(() => {
    let isMounted = true;
    async function loadDetail() {
      if (!selectedId) {
        setDetail(null);
        return;
      }
      setDetailLoading(true);
      try {
        const data = demo
          ? { session: demoReviewSession, events: demoReviewEvents }
          : await getChetzelsReviewSession(selectedId);
        if (isMounted) setDetail({ session: data.session, events: data.events || [] });
      } catch (requestError) {
        if (isMounted) setError(requestError.message);
      } finally {
        if (isMounted) setDetailLoading(false);
      }
    }
    loadDetail();
    return () => {
      isMounted = false;
    };
  }, [selectedId, demo, setError]);

  async function reload(message = '') {
    if (demo) {
      setSessions([demoReviewSession]);
      setDetail({ session: demoReviewSession, events: demoReviewEvents });
      if (message) setNotice(message);
      return;
    }
    const data = await getChetzelsReviewSessions({ limit: 100 });
    setSessions(data.sessions || []);
    if (selectedId) {
      const nextDetail = await getChetzelsReviewSession(selectedId);
      setDetail({ session: nextDetail.session, events: nextDetail.events || [] });
    }
    if (message) setNotice(message);
  }

  return (
    <div className="review-session-shell">
      <section className="metric-row" aria-label="Wireframe review summary">
        <Metric label="Review sessions" value={metrics.sessions} icon={MessageSquare} />
        <Metric label="Logged events" value={metrics.totalEvents} icon={ClipboardList} />
        <Metric label="Selected notes" value={metrics.currentNotes} icon={FileText} />
        <Metric label="Chat turns" value={metrics.currentChatTurns} icon={Send} />
      </section>

      <section className="panel wide">
        <PanelHead
          title="Chetzels Wireframe Reviews"
          action={loading ? 'Loading' : `${visibleSessions.length} visible`}
        />
        <div className="review-session-toolbar">
          <label className="search-control">
            <Search size={16} />
            <span className="sr-only">Search review sessions</span>
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search reviewer, session, or URL..."
            />
          </label>
          <button className="icon-btn" type="button" onClick={() => reload('Review sessions refreshed.')} title="Refresh review sessions">
            <RefreshCw size={18} />
          </button>
        </div>
      </section>

      <div className="review-session-grid">
        <section className="panel review-session-list-panel">
          <PanelHead title="Sessions" action={loading ? 'Loading' : `${visibleSessions.length}`} />
          <div className="review-session-list">
            {visibleSessions.map((session) => (
              <ReviewSessionRow
                key={session.id}
                session={session}
                selected={selectedSession?.id === session.id}
                onSelect={setSelectedId}
              />
            ))}
            {!visibleSessions.length ? (
              <EmptyState
                title={sessions.length ? 'No matching sessions' : 'No review sessions yet'}
                message={sessions.length ? 'Try a different search term.' : 'Karina comments will appear here after she uses the wireframe assistant.'}
              />
            ) : null}
          </div>
        </section>

        <section className="panel review-session-detail-panel">
          <PanelHead title="Selected Review" action={detailLoading ? 'Loading' : selectedSession ? formatDateTime(selectedSession.lastSeenAt) : ''} />
          {selectedSession ? (
            <ReviewSessionDetail
              session={selectedSession}
              events={events}
              setNotice={setNotice}
              setError={setError}
            />
          ) : (
            <EmptyState title="Select a review session" message="Saved notes and chat history will appear here." />
          )}
        </section>
      </div>
    </div>
  );
}

function ReviewSessionRow({ session, selected, onSelect }) {
  return (
    <button
      className={`review-session-row ${selected ? 'active' : ''}`}
      type="button"
      onClick={() => onSelect(session.id)}
    >
      <div>
        <strong>{session.visitorLabel || 'Karina'}</strong>
        <span>{formatDateTime(session.lastSeenAt)} / {session.eventCount || 0} events</span>
      </div>
      <small>{session.id}</small>
    </button>
  );
}

function ReviewSessionDetail({ session, events, setNotice, setError }) {
  const notes = getReviewNotes(events);
  const chat = getReviewChat(events);
  const copiedEvents = events.filter((event) => event.eventType === 'brief_copied');

  async function copyBrief() {
    const brief = buildWireframeChangeBrief(session, events);
    try {
      await navigator.clipboard.writeText(brief);
      setNotice('Chetzels review brief copied.');
    } catch {
      setError('Could not copy the review brief from this browser.');
    }
  }

  return (
    <div className="review-session-detail">
      <div className="bottleneck-detail-head">
        <div>
          <div className="row-title">
            <h2>{session.visitorLabel || 'Karina'}</h2>
            <StatusPill status={session.status || 'active'} />
          </div>
          <p>{session.id}</p>
        </div>
        <div className="mini-actions">
          {session.pageUrl ? (
            <a className="mini-link" href={session.pageUrl} target="_blank" rel="noreferrer">
              Wireframe <ArrowUpRight size={14} />
            </a>
          ) : null}
          <button className="btn primary" type="button" onClick={copyBrief}>
            <Copy size={17} />
            <span>Copy Brief</span>
          </button>
        </div>
      </div>

      <div className="bottleneck-summary">
        <Answer label="First seen" value={formatDateTime(session.firstSeenAt)} />
        <Answer label="Last seen" value={formatDateTime(session.lastSeenAt)} />
        <Answer label="Saved notes" value={notes.length} />
        <Answer label="Brief copies" value={copiedEvents.length} />
      </div>

      <section className="bottleneck-copy-block">
        <h3>Implementation Brief</h3>
        {notes.length ? (
          <ul>
            {notes.map((note) => (
              <li key={note.id}>
                <strong>{note.section}:</strong> {note.text}
              </li>
            ))}
          </ul>
        ) : (
          <p>No saved notes yet. Chat messages still appear below.</p>
        )}
      </section>

      <div className="bottleneck-subgrid">
        <section>
          <h3>Saved Notes</h3>
          <div className="bottleneck-note-list">
            {notes.map((note) => (
              <p key={note.id}>
                <strong>{note.section}</strong><br />
                {note.text}
              </p>
            ))}
            {!notes.length ? <EmptyState title="No notes" message="Section comments from the wireframe will appear here." /> : null}
          </div>
        </section>

        <section>
          <h3>Conversation</h3>
          <div className="review-chat-list">
            {chat.map((message) => (
              <article className={`review-chat-message ${message.role}`} key={message.id}>
                <strong>{message.role === 'assistant' ? 'Assistant' : 'Karina'} / {message.section}</strong>
                <p>{message.text}</p>
                <small>{formatDateTime(message.createdAt)}</small>
              </article>
            ))}
            {!chat.length ? <EmptyState title="No chat yet" message="AI conversation turns will appear here." /> : null}
          </div>
        </section>
      </div>

      <section>
        <h3>Event Timeline</h3>
        <div className="timeline-list">
          {events.map((event) => (
            <div className="timeline-item" key={event.id}>
              <span className="status queued">{formatDateTime(event.createdAt)}</span>
              <div>
                <strong>{titleize(event.eventType)}</strong>
                <p>{event.message || event.section || 'Logged event'}</p>
                <small>{[event.role, event.section].filter(Boolean).join(' / ')}</small>
              </div>
            </div>
          ))}
          {!events.length ? <EmptyState title="No events" message="Logged review activity will appear here." /> : null}
        </div>
      </section>
    </div>
  );
}

function BottlenecksView({ setSelectedClientId, setNotice, setError, demo }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [fitFilter, setFitFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [data, setData] = useState({ leads: [], metrics: {} });
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  const [patchForm, setPatchForm] = useState({ status: 'new', nextAction: '', nextFollowupAt: '', notes: '' });

  useEffect(() => {
    let isMounted = true;
    async function load() {
      setLoading(true);
      try {
        const result = demo
          ? demoBottleneckData
          : await getBottleneckLeads({ status: statusFilter, fitTier: fitFilter, q: searchQuery, limit: 150 });
        if (!isMounted) return;
        setData({ leads: result.leads || [], metrics: result.metrics || {} });
        setSelectedId((current) => {
          if (current && result.leads?.some((lead) => lead.id === current)) return current;
          return result.leads?.[0]?.id || '';
        });
      } catch (requestError) {
        if (isMounted) setError(requestError.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    const timer = window.setTimeout(load, demo ? 0 : 180);
    return () => {
      isMounted = false;
      window.clearTimeout(timer);
    };
  }, [statusFilter, fitFilter, searchQuery, demo, setError]);

  const visibleLeads = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return data.leads.filter((lead) => {
      const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
      const matchesFit = fitFilter === 'all' || lead.fitTier === fitFilter;
      const matchesSearch = !query || [lead.company, lead.email, lead.firstName, lead.lastName, lead.industry].join(' ').toLowerCase().includes(query);
      return matchesStatus && matchesFit && matchesSearch;
    });
  }, [data.leads, statusFilter, fitFilter, searchQuery]);

  const selectedLead = detail?.lead || visibleLeads.find((lead) => lead.id === selectedId) || visibleLeads[0] || null;
  const metrics = data.metrics || {};

  useEffect(() => {
    let isMounted = true;
    async function loadDetail() {
      if (!selectedId) {
        setDetail(null);
        return;
      }
      try {
        const result = demo
          ? {
              lead: demoBottleneckData.leads.find((lead) => lead.id === selectedId) || demoBottleneckLead,
              followups: [
                { id: 'demo-fup-1', step: 'immediate', status: 'sent', dueAt: new Date().toISOString(), subject: 'Your JJE Bottleneck Review is in' },
                { id: 'demo-fup-2', step: 'day2', status: 'queued', dueAt: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(), subject: 'Still want help finding the bottleneck?' },
                { id: 'demo-task-1', step: 'staff_task', status: 'queued', dueAt: new Date().toISOString(), subject: 'Review hot Bottleneck lead' }
              ],
              events: [
                { id: 'demo-evt-1', eventType: 'lead_submitted', createdAt: new Date().toISOString(), metadata: { fitTier: 'Hot / Sales Ready' } }
              ]
            }
          : await getBottleneckLead(selectedId);
        if (isMounted) setDetail(result);
      } catch (requestError) {
        if (isMounted) setError(requestError.message);
      }
    }

    loadDetail();
    return () => {
      isMounted = false;
    };
  }, [selectedId, demo, setError]);

  useEffect(() => {
    if (!selectedLead) return;
    setPatchForm({
      status: selectedLead.status || 'new',
      nextAction: selectedLead.nextAction || '',
      nextFollowupAt: toDateTimeInputValue(selectedLead.nextFollowupAt),
      notes: selectedLead.notes || ''
    });
  }, [selectedLead?.id, selectedLead?.status, selectedLead?.nextAction, selectedLead?.nextFollowupAt, selectedLead?.notes]);

  async function reload(message = '') {
    if (demo) {
      if (message) setNotice(message);
      return;
    }
    const result = await getBottleneckLeads({ status: statusFilter, fitTier: fitFilter, q: searchQuery, limit: 150 });
    setData({ leads: result.leads || [], metrics: result.metrics || {} });
    if (selectedId) setDetail(await getBottleneckLead(selectedId));
    if (message) setNotice(message);
  }

  async function handlePatch(event) {
    event.preventDefault();
    if (!selectedLead) return;
    setWorking('patch');
    try {
      if (!demo) {
        await updateBottleneckLead(selectedLead.id, {
          status: patchForm.status,
          nextAction: patchForm.nextAction,
          nextFollowupAt: patchForm.nextFollowupAt ? new Date(patchForm.nextFollowupAt).toISOString() : null,
          notes: patchForm.notes
        });
        await reload('Bottleneck lead updated.');
      } else {
        setNotice('Demo bottleneck lead updated.');
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setWorking('');
    }
  }

  async function handleConvert() {
    if (!selectedLead) return;
    const confirmed = window.confirm(`Convert ${selectedLead.company} into a client workspace?`);
    if (!confirmed) return;
    setWorking('convert');
    try {
      const result = demo ? { clientId: 'demo-client' } : await convertBottleneckLead(selectedLead.id);
      if (result.clientId) setSelectedClientId(result.clientId);
      await reload('Bottleneck lead converted to client workspace.');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setWorking('');
    }
  }

  return (
    <div className="bottleneck-shell">
      <section className="metric-row" aria-label="Bottleneck summary">
        <Metric label="Total leads" value={metrics.total || data.leads.length} icon={ClipboardList} />
        <Metric label="Hot leads" value={metrics.hot || 0} icon={Sparkles} />
        <Metric label="Warm leads" value={metrics.warm || 0} icon={Clock3} />
        <Metric label="Converted" value={metrics.converted || 0} icon={CheckCircle2} />
      </section>

      <section className="panel wide">
        <PanelHead
          title="Bottleneck Intake"
          action={`${visibleLeads.length} visible`}
        />
        <div className="bottleneck-toolbar">
          <label className="search-control">
            <Search size={16} />
            <span className="sr-only">Search bottleneck leads</span>
            <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Search leads..." />
          </label>
          <select value={fitFilter} onChange={(event) => setFitFilter(event.target.value)}>
            {bottleneckFitTiers.map((tier) => <option key={tier} value={tier}>{tier === 'all' ? 'All fit tiers' : tier}</option>)}
          </select>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            {bottleneckStatuses.map((status) => <option key={status} value={status}>{status === 'all' ? 'All statuses' : titleize(status)}</option>)}
          </select>
          <button className="icon-btn" type="button" onClick={() => reload('Bottleneck leads refreshed.')} title="Refresh bottleneck leads">
            <RefreshCw size={18} />
          </button>
        </div>
      </section>

      <div className="bottleneck-grid">
        <section className="panel bottleneck-list-panel">
          <PanelHead title="Leads" action={loading ? 'Loading' : `${visibleLeads.length}`} />
          <div className="bottleneck-list">
            {visibleLeads.map((lead) => (
              <button
                key={lead.id}
                type="button"
                className={`bottleneck-lead-row ${selectedLead?.id === lead.id ? 'active' : ''}`}
                onClick={() => setSelectedId(lead.id)}
              >
                <div>
                  <strong>{lead.company}</strong>
                  <span>{lead.firstName} {lead.lastName} / {lead.industry}</span>
                </div>
                <div>
                  <span className={`fit-pill ${getBottleneckTone(lead.fitTier)}`}>{lead.fitTier}</span>
                  <small>{lead.totalScore} pts</small>
                </div>
              </button>
            ))}
            {!visibleLeads.length ? <EmptyState title="No bottleneck leads" message="Submitted reviews will appear here." /> : null}
          </div>
        </section>

        <section className="panel bottleneck-detail-panel">
          {selectedLead ? (
            <BottleneckDetail
              lead={selectedLead}
              detail={detail}
              patchForm={patchForm}
              setPatchForm={setPatchForm}
              working={working}
              onPatch={handlePatch}
              onConvert={handleConvert}
            />
          ) : (
            <EmptyState title="Select a lead" message="Choose a bottleneck review to inspect the score, answers, and follow-up timeline." />
          )}
        </section>
      </div>
    </div>
  );
}

function BottleneckDetail({ lead, detail, patchForm, setPatchForm, working, onPatch, onConvert }) {
  const summary = lead.publicSummary || {};
  const internal = lead.internalSummary || {};
  const followups = detail?.followups || [];
  const events = detail?.events || [];
  const scoreEntries = Object.entries(lead.scoreBreakdown || {});

  return (
    <div className="bottleneck-detail">
      <div className="bottleneck-detail-head">
        <div>
          <p className="section-kicker">{lead.fitTier}</p>
          <h2>{lead.company}</h2>
          <p>{lead.firstName} {lead.lastName} / {lead.title} / {lead.email}</p>
        </div>
        <div className="bottleneck-score">
          <strong>{lead.totalScore}</strong>
          <span>Fit score</span>
        </div>
      </div>

      <div className="bottleneck-summary">
        <div>
          <span>Recommended offer</span>
          <strong>{lead.recommendedOffer}</strong>
        </div>
        <div>
          <span>Next action</span>
          <strong>{lead.nextAction || 'Manual review'}</strong>
        </div>
      </div>

      <div className="bottleneck-copy-block">
        <h3>{summary.headline || 'Review summary'}</h3>
        <p>{summary.diagnosis || lead.successOutcome}</p>
        {summary.quickWins?.length ? (
          <ul>{summary.quickWins.map((item) => <li key={item}>{item}</li>)}</ul>
        ) : null}
      </div>

      <div className="bottleneck-subgrid">
        <section>
          <h3>Score Breakdown</h3>
          <div className="score-grid compact">
            {scoreEntries.map(([key, value]) => (
              <div className="score-tile" key={key}>
                <span>{titleize(key)}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h3>Internal Notes</h3>
          <div className="bottleneck-note-list">
            {(internal.callPrepNotes || []).map((note) => <p key={note}>{note}</p>)}
            {internal.recommendedAngle ? <p>{internal.recommendedAngle}</p> : null}
            {internal.riskFlags?.length ? internal.riskFlags.map((flag) => <p key={flag}>{flag}</p>) : null}
          </div>
        </section>
      </div>

      <div className="bottleneck-answer-grid">
        <Answer label="Primary bottleneck" value={(lead.primaryBottleneck || []).join(', ')} />
        <Answer label="Lead leakage" value={lead.leadLeakage} />
        <Answer label="Time lost" value={lead.timeLostWeek} />
        <Answer label="Tools" value={(lead.currentTools || []).join(', ')} />
        <Answer label="Connectivity" value={lead.toolConnectivity} />
        <Answer label="Urgency" value={lead.urgency} />
        <Answer label="AI follow-up question" value={lead.aiFollowupQuestion} />
        <Answer label="AI follow-up answer" value={lead.aiFollowupAnswer} />
        <Answer label="Budget" value={lead.budgetFit} />
        <Answer label="Outcome" value={lead.successOutcome} />
      </div>

      <div className="bottleneck-subgrid">
        <section>
          <h3>Follow-up Timeline</h3>
          <div className="timeline-list">
            {followups.map((item) => (
              <div className="timeline-item" key={item.id}>
                <span className={`status ${String(item.status || '').replace(/_/g, '-')}`}>{titleize(item.status)}</span>
                <div>
                  <strong>{titleize(item.step)}</strong>
                  <p>{item.subject || item.templateKey}</p>
                  <small>{formatDate(item.dueAt)}{item.sentAt ? ` / sent ${formatDate(item.sentAt)}` : ''}</small>
                </div>
              </div>
            ))}
            {!followups.length ? <EmptyState title="No timeline yet" message="Follow-up emails and staff tasks will appear here." /> : null}
          </div>
        </section>
        <section>
          <h3>Events</h3>
          <div className="timeline-list">
            {events.map((event) => (
              <div className="timeline-item" key={event.id}>
                <span className="status queued">{formatDate(event.createdAt)}</span>
                <div>
                  <strong>{titleize(event.eventType)}</strong>
                  <p>{event.metadata ? JSON.stringify(event.metadata) : ''}</p>
                </div>
              </div>
            ))}
            {!events.length ? <EmptyState title="No events" message="Submission, email, unsubscribe, and conversion events will appear here." /> : null}
          </div>
        </section>
      </div>

      <form className="bottleneck-admin-form" onSubmit={onPatch}>
        <Select label="Status" value={patchForm.status} onChange={(value) => setPatchForm({ ...patchForm, status: value })} options={bottleneckStatuses.filter((status) => status !== 'all')} />
        <Input label="Next follow-up" type="datetime-local" value={patchForm.nextFollowupAt} onChange={(value) => setPatchForm({ ...patchForm, nextFollowupAt: value })} />
        <Textarea label="Next action" value={patchForm.nextAction} onChange={(value) => setPatchForm({ ...patchForm, nextAction: value })} />
        <Textarea label="Staff notes" value={patchForm.notes} onChange={(value) => setPatchForm({ ...patchForm, notes: value })} />
        <div className="button-row">
          <button className="btn primary" type="submit" disabled={working === 'patch'}>Save Review</button>
          <button className="btn" type="button" onClick={onConvert} disabled={working === 'convert' || lead.status === 'converted'}>
            Convert to Client
          </button>
        </div>
      </form>
    </div>
  );
}

function Answer({ label, value }) {
  return (
    <div className="answer-tile">
      <span>{label}</span>
      <strong>{value || 'Not provided'}</strong>
    </div>
  );
}

function getBottleneckTone(fitTier = '') {
  if (fitTier.startsWith('Hot')) return 'hot';
  if (fitTier.startsWith('Warm')) return 'warm';
  if (fitTier.startsWith('Nurture')) return 'nurture';
  return 'muted';
}

function toDateTimeInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

const revenueStatuses = ['all', 'prospect', 'contacted', 'audit_sent', 'meeting_scheduled', 'proposal_sent', 'won', 'lost'];
const revenuePipelineStatuses = revenueStatuses.filter((status) => status !== 'all');
const revenueQueueViews = [
  { id: 'best_fit', label: 'Best Fit' },
  { id: 'needs_audit', label: 'Needs Audit' },
  { id: 'ready_outreach', label: 'Ready for Outreach' },
  { id: 'follow_up', label: 'Follow-up Due' },
  { id: 'meetings', label: 'Meetings' },
  { id: 'won_lost', label: 'Won/Lost' }
];
const revenueDetailTabs = [
  { id: 'action_plan', label: 'Action Plan' },
  { id: 'audit', label: 'Audit' },
  { id: 'outreach', label: 'Outreach' },
  { id: 'proposal', label: 'Proposal' },
  { id: 'notes', label: 'Notes' }
];
const revenueSortOptions = [
  { value: 'grade_desc', label: 'Grade: highest first' },
  { value: 'grade_asc', label: 'Grade: lowest first' },
  { value: 'score_asc', label: 'Score: biggest opportunity' },
  { value: 'score_desc', label: 'Score: strongest site' },
  { value: 'status_asc', label: 'Pipeline status' },
  { value: 'city_asc', label: 'City' },
  { value: 'name_asc', label: 'Business name' }
];
const revenueCities = ['Brentwood', 'Franklin', 'Nashville'];
const revenueIndustries = ['Attorneys', 'CPAs', 'Wealth Advisors', 'Insurance Agencies', 'Medical Practices', 'Home Service Companies', 'Professional Services'];

const emptyRevenueCompany = {
  businessName: '',
  website: '',
  industry: 'Professional Services',
  city: 'Franklin',
  email: '',
  phone: '',
  googleRating: '',
  notes: ''
};

const demoRevenueData = {
  companies: [
    {
      id: 'demo-rev-1',
      businessName: 'Sample Legal Practice',
      website: 'https://example.com',
      industry: 'Attorneys',
      city: 'Franklin',
      email: 'hello@example.com',
      phone: '615-555-0100',
      googleRating: 4.7,
      status: 'prospect',
      prospectGrade: 'A',
      overallScore: 52,
      leadGenerationScore: 42,
      automationScore: 36,
      lastAnalyzedAt: new Date().toISOString()
    },
    {
      id: 'demo-rev-2',
      businessName: 'Sample Advisory Firm',
      website: 'https://example.org',
      industry: 'Wealth Advisors',
      city: 'Brentwood',
      email: 'team@example.org',
      phone: '615-555-0188',
      googleRating: 4.9,
      status: 'audit_sent',
      prospectGrade: 'B',
      overallScore: 69,
      leadGenerationScore: 66,
      automationScore: 58,
      lastAnalyzedAt: new Date().toISOString()
    }
  ],
  metrics: {
    prospectsCreated: 2,
    auditsGenerated: 2,
    emailsSent: 0,
    meetingsBooked: 0,
    dealsWon: 0,
    revenueCents: 0
  }
};

const demoAudit = {
  id: 'demo-audit',
  prospectGrade: 'A',
  scores: {
    designScore: 58,
    seoScore: 48,
    speedScore: 74,
    mobileScore: 62,
    leadGenerationScore: 42,
    automationScore: 36,
    overallScore: 53
  },
  notes: 'This demo scorecard is based on public website signals and highlights conversion opportunities without using private analytics.',
  topImprovements: [
    'Make the primary call to action obvious above the fold.',
    'Add a short, mobile-friendly inquiry form.',
    'Bring reviews and credentials closer to the first decision point.'
  ],
  quickWins: [
    'Turn the phone number into a tap-to-call link.',
    'Add a concise meta description for local search.',
    'Move the strongest service area language higher on the page.'
  ],
  automationOpportunities: [
    'Add appointment scheduling with automatic reminders.',
    'Send instant confirmation messages after each inquiry.',
    'Track source, service need, and urgency for each new opportunity.'
  ],
  loomScript: 'Hi, this is John with JJE Creative. I was looking at this website and saw a few practical opportunities to turn more local visitors into real conversations.',
  markdown: '# Demo Website Scorecard\n\nUse Generate Audit on a real prospect to create a fresh scorecard.',
  outreachDrafts: [
    {
      step: 1,
      subject: 'Quick website idea',
      body: 'I noticed a practical opportunity to make the site work harder for local visitors. Open to me sending a short scorecard?'
    }
  ]
};

function RevenueView({ setNotice, setError, demo }) {
  const [queueId, setQueueId] = useState('best_fit');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState('grade_desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [detailTab, setDetailTab] = useState('action_plan');
  const [showProspectModal, setShowProspectModal] = useState(false);
  const [revenueData, setRevenueData] = useState({ companies: [], metrics: {} });
  const [selectedId, setSelectedId] = useState('');
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [discoveryPlan, setDiscoveryPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');

  const visibleCompanies = useMemo(() => (
    sortRevenueCompanies(
      filterRevenueCompanies(
        filterRevenueCompaniesByPipeline(
          filterRevenueCompaniesByQueue(revenueData.companies, queueId),
          'all',
          statusFilter
        ),
        searchQuery
      ),
      sortKey
    )
  ), [revenueData.companies, queueId, statusFilter, searchQuery, sortKey]);
  const pipelineSummary = useMemo(() => summarizeRevenueCompanies(revenueData.companies), [revenueData.companies]);
  const commandSummary = useMemo(() => (
    getRevenueCommandSummary({ metrics: revenueData.metrics, companies: revenueData.companies })
  ), [revenueData.metrics, revenueData.companies]);
  const hasActiveProspectFilters = Boolean(searchQuery.trim() || statusFilter !== 'all' || queueId !== 'best_fit');
  const selectedCompany = (
    visibleCompanies.find((company) => company.id === selectedId)
    || (!hasActiveProspectFilters ? revenueData.companies.find((company) => company.id === selectedId) : null)
    || visibleCompanies[0]
    || (!hasActiveProspectFilters ? revenueData.companies[0] : null)
    || null
  );

  useEffect(() => {
    let isMounted = true;
    async function load() {
      setLoading(true);
      try {
        const data = demo ? demoRevenueData : await getRevenueCompanies({ limit: 150 });
        if (!isMounted) return;
        setRevenueData({ companies: data.companies || [], metrics: data.metrics || {} });
        setSelectedId((current) => {
          if (current && data.companies?.some((company) => company.id === current)) return current;
          return data.companies?.[0]?.id || '';
        });
      } catch (requestError) {
        if (isMounted) setError(requestError.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [demo, setError]);

  useEffect(() => {
    let isMounted = true;
    async function loadAudit() {
      if (!selectedCompany) {
        setSelectedAudit(null);
        return;
      }
      if (demo) {
        setSelectedAudit(demoAudit);
        return;
      }
      if (!selectedCompany.latestAuditId) {
        setSelectedAudit(null);
        return;
      }
      try {
        const result = await getRevenueAudit(selectedCompany.id);
        if (isMounted) setSelectedAudit(result.audit);
      } catch {
        if (isMounted) setSelectedAudit(null);
      }
    }

    loadAudit();
    return () => {
      isMounted = false;
    };
  }, [selectedCompany?.id, selectedCompany?.latestAuditId, demo]);

  async function reloadRevenue(message = '') {
    const data = demo ? revenueData : await getRevenueCompanies({ limit: 150 });
    if (!demo) setRevenueData({ companies: data.companies || [], metrics: data.metrics || {} });
    if (message) setNotice(message);
  }

  async function handleCreateCompany(company) {
    setWorking('create');
    try {
      if (demo) {
        const created = { ...company, id: `demo-${Date.now()}`, status: 'prospect', prospectGrade: '', overallScore: null };
        setRevenueData((current) => ({
          ...current,
          companies: [created, ...current.companies],
          metrics: { ...current.metrics, prospectsCreated: Number(current.metrics.prospectsCreated || 0) + 1 }
        }));
        setSelectedId(created.id);
      } else {
        const result = await createRevenueCompanies(company);
        const first = result.results?.find((item) => item.ok);
        if (first?.id) setSelectedId(first.id);
        await reloadRevenue();
      }
      setNotice('Prospect saved.');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setWorking('');
    }
  }

  async function handleImport(text) {
    const companies = parseProspectImport(text);
    if (!companies.length) {
      setError('Paste at least one prospect row before importing.');
      return;
    }

    setWorking('import');
    try {
      if (!demo) await createRevenueCompanies({ companies });
      await reloadRevenue(`${companies.length} prospect${companies.length === 1 ? '' : 's'} imported.`);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setWorking('');
    }
  }

  async function handleDiscovery(cities, industries) {
    setWorking('discovery');
    try {
      const result = demo
        ? { plan: buildDemoDiscoveryPlan(cities, industries) }
        : await getRevenueDiscovery({ cities, industries });
      setDiscoveryPlan(result.plan);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setWorking('');
    }
  }

  async function handleGenerateAudit() {
    if (!selectedCompany) return;
    setWorking('audit');
    try {
      const result = demo ? { audit: demoAudit } : await generateRevenueAudit(selectedCompany.id);
      setSelectedAudit(result.audit);
      await reloadRevenue(`Audit generated for ${selectedCompany.businessName}.`);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setWorking('');
    }
  }

  async function handleStatusUpdate(nextStatus) {
    if (!selectedCompany) return;
    setWorking('status');
    try {
      if (demo) {
        setRevenueData((current) => ({
          ...current,
          companies: current.companies.map((company) => (
            company.id === selectedCompany.id ? { ...company, status: nextStatus } : company
          ))
        }));
      } else {
        await updateRevenueCompany(selectedCompany.id, { status: nextStatus });
        await reloadRevenue('Pipeline updated.');
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setWorking('');
    }
  }

  return (
    <div className="view-grid revenue-shell">
      <div className="revenue-command-center">
        <section className="panel wide revenue-command-panel">
          <PanelHead
            title="Revenue Command Center"
            action={(
              <>
                <span>{loading ? 'Loading' : `${visibleCompanies.length}/${pipelineSummary.total} in queue`}</span>
                <button className="btn compact" type="button" onClick={() => setShowProspectModal(true)}>
                  <Plus size={16} />
                  <span>Add / Import</span>
                </button>
              </>
            )}
          />
          <RevenueCommandBand items={commandSummary} />
          <div className="revenue-command-grid">
            <section className="revenue-queue-panel" aria-label="Daily prospect queue">
              <div className="revenue-section-head">
                <div>
                  <span>Today's Queue</span>
                  <strong>{visibleCompanies.length} prospect{visibleCompanies.length === 1 ? '' : 's'}</strong>
                </div>
                <span>{revenueQueueViews.find((view) => view.id === queueId)?.label || 'Best Fit'}</span>
              </div>
              <RevenueQueueControls
                queueId={queueId}
                setQueueId={setQueueId}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                sortKey={sortKey}
                setSortKey={setSortKey}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
              />
              <RevenueQueue
                companies={visibleCompanies}
                selectedId={selectedCompany?.id || ''}
                onSelect={setSelectedId}
                hasFilters={hasActiveProspectFilters}
              />
            </section>

            <section className="revenue-workspace-panel" id="revenue-detail" aria-label="Selected prospect workspace">
              <RevenueProspectWorkspace
                audit={selectedAudit}
                company={selectedCompany}
                disabled={Boolean(working)}
                activeTab={detailTab}
                setActiveTab={setDetailTab}
                onGenerateAudit={handleGenerateAudit}
                onStatusUpdate={handleStatusUpdate}
                setError={setError}
              />
            </section>
          </div>
        </section>

        <section className="panel wide revenue-discovery-panel">
          <details className="revenue-discovery-drawer">
            <summary>
              <span>Find Prospects</span>
              <strong>Local search plans</strong>
            </summary>
            <RevenueDiscovery
              disabled={Boolean(working)}
              plan={discoveryPlan}
              onGenerate={handleDiscovery}
            />
          </details>
        </section>
      </div>

      <RevenueProspectModal
        open={showProspectModal}
        disabled={Boolean(working)}
        working={working}
        onClose={() => setShowProspectModal(false)}
        onCreate={async (company) => {
          await handleCreateCompany(company);
          setShowProspectModal(false);
        }}
        onImport={async (text) => {
          await handleImport(text);
          setShowProspectModal(false);
        }}
      />
    </div>
  );
}

function RevenueCommandBand({ items }) {
  return (
    <div className="revenue-command-band" aria-label="Daily revenue summary">
      {items.map(({ label, value }) => (
        <div key={label}>
          <strong>{value}</strong>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

function RevenueQueueControls({
  queueId,
  setQueueId,
  searchQuery,
  setSearchQuery,
  sortKey,
  setSortKey,
  statusFilter,
  setStatusFilter
}) {
  return (
    <>
      <div className="revenue-queue-tabs" role="list" aria-label="Smart revenue queues">
        {revenueQueueViews.map((view) => (
          <button
            className={queueId === view.id ? 'active' : ''}
            key={view.id}
            type="button"
            onClick={() => setQueueId(view.id)}
          >
            {view.label}
          </button>
        ))}
      </div>
      <div className="revenue-toolbar">
        <label className="search-control">
          <Search size={16} />
          <span className="sr-only">Search prospects</span>
          <input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search prospects..."
          />
        </label>
        <label className="sort-control">
          <ArrowDownWideNarrow size={16} />
          <span>Sort</span>
          <select value={sortKey} onChange={(event) => setSortKey(event.target.value)}>
            {revenueSortOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <label className="sort-control status-control">
          <span>Status</span>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            {revenueStatuses.map((status) => (
              <option key={status} value={status}>{status === 'all' ? 'Any status' : titleize(status)}</option>
            ))}
          </select>
        </label>
      </div>
    </>
  );
}

function RevenueProspectModal({ open, disabled, working, onClose, onCreate, onImport }) {
  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className="modal-panel revenue-modal" role="dialog" aria-modal="true" aria-labelledby="revenue-prospect-modal-title">
        <PanelHead
          title="Add or Import Prospects"
          action={(
            <button className="mini-link inline-button" type="button" onClick={onClose}>
              Close
            </button>
          )}
        />
        <h2 className="sr-only" id="revenue-prospect-modal-title">Add or Import Prospects</h2>
        <RevenueAddForm disabled={disabled} onCreate={onCreate} onImport={onImport} working={working} />
      </section>
    </div>
  );
}

function RevenueAddForm({ disabled, onCreate, onImport, working }) {
  const [form, setForm] = useState(emptyRevenueCompany);
  const [importText, setImportText] = useState('');

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  return (
    <div className="revenue-form-stack">
      <form
        className="revenue-form"
        onSubmit={async (event) => {
          event.preventDefault();
          await onCreate(form);
          setForm(emptyRevenueCompany);
        }}
      >
        <Input label="Business name" value={form.businessName} onChange={(value) => update('businessName', value)} required />
        <Input label="Website" value={form.website} onChange={(value) => update('website', value)} />
        <Select label="Industry" value={form.industry} onChange={(value) => update('industry', value)} options={revenueIndustries} />
        <Select label="City" value={form.city} onChange={(value) => update('city', value)} options={revenueCities} />
        <Input label="Email" type="email" value={form.email} onChange={(value) => update('email', value)} />
        <Input label="Phone" value={form.phone} onChange={(value) => update('phone', value)} />
        <Input label="Google rating" type="number" value={form.googleRating} onChange={(value) => update('googleRating', value)} />
        <Textarea label="Notes" value={form.notes} onChange={(value) => update('notes', value)} />
        <button className="btn primary" type="submit" disabled={disabled || !form.businessName}>
          <Plus size={17} />
          <span>{working === 'create' ? 'Saving' : 'Save Prospect'}</span>
        </button>
      </form>

      <div className="import-box">
        <label>
          <span>Paste prospects</span>
          <textarea
            value={importText}
            onChange={(event) => setImportText(event.target.value)}
            rows={5}
            placeholder="Business, Website, Industry, City, Email, Phone, Rating, Notes"
          />
        </label>
        <button
          className="btn"
          type="button"
          disabled={disabled || !importText.trim()}
          onClick={async () => {
            await onImport(importText);
            setImportText('');
          }}
        >
          <FileUp size={17} />
          <span>{working === 'import' ? 'Importing' : 'Import Rows'}</span>
        </button>
      </div>
    </div>
  );
}

function RevenueDiscovery({ disabled, plan, onGenerate }) {
  const [city, setCity] = useState('Franklin');
  const [industry, setIndustry] = useState('Attorneys');

  return (
    <div className="discovery-stack">
      <div className="form-grid compact">
        <Select label="City" value={city} onChange={setCity} options={revenueCities} />
        <Select label="Industry" value={industry} onChange={setIndustry} options={revenueIndustries} />
      </div>
      <div className="button-row">
        <button className="btn primary" type="button" disabled={disabled} onClick={() => onGenerate([city], [industry])}>
          <Sparkles size={17} />
          <span>Build Search Plan</span>
        </button>
        <button className="btn" type="button" disabled={disabled} onClick={() => onGenerate(revenueCities, revenueIndustries)}>
          <RefreshCw size={17} />
          <span>All Markets</span>
        </button>
      </div>
      {plan?.tasks?.length ? (
        <div className="discovery-results">
          {plan.tasks.slice(0, 12).map((task) => (
            <article className="discovery-task" key={task.id}>
              <div>
                <strong>{task.industry}</strong>
                <span>{task.city}</span>
              </div>
              <div className="mini-actions">
                <a className="mini-link" href={task.searchUrl} target="_blank" rel="noreferrer">
                  Search <ArrowUpRight size={14} />
                </a>
                <a className="mini-link" href={task.mapsUrl} target="_blank" rel="noreferrer">
                  Maps <ArrowUpRight size={14} />
                </a>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <EmptyState title="No discovery plan yet" message="Choose a market and industry to create search tasks." />
      )}
    </div>
  );
}

function RevenueQueue({ companies, selectedId, onSelect, hasFilters }) {
  if (!companies.length) {
    return (
      <EmptyState
        title={hasFilters ? 'No matching prospects' : 'No prospects yet'}
        message={hasFilters ? 'Try another queue, status, or search term to see more prospects.' : 'Add a business or import rows to start building the engine.'}
      />
    );
  }

  return (
    <div className="revenue-queue" aria-label="Revenue prospects">
      {companies.map((company) => (
        <RevenueQueueRow company={company} key={company.id} onSelect={onSelect} selected={selectedId === company.id} />
      ))}
    </div>
  );
}

function RevenueQueueRow({ company, onSelect, selected }) {
  const nextAction = getRevenueNextAction(company);

  return (
    <button
      className={`revenue-queue-row ${selected ? 'active' : ''}`}
      type="button"
      onClick={() => onSelect(company.id)}
    >
      <div className="queue-company">
        <strong>{company.businessName}</strong>
        <span>{[company.industry || 'Industry pending', company.city || 'City pending'].join(' / ')}</span>
      </div>
      <div className="queue-status">
        <GradePill grade={company.prospectGrade} />
        <span>{company.overallScore ? `${company.overallScore}/100` : 'Not audited'}</span>
      </div>
      <StatusPill status={company.status || 'prospect'} />
      <div className={`queue-action action-${nextAction.tone}`}>
        <span>{nextAction.label}</span>
      </div>
    </button>
  );
}

function RevenueProspectWorkspace({ audit, company, disabled, activeTab, setActiveTab, onGenerateAudit, onStatusUpdate, setError }) {
  if (!company) return <EmptyState title="Select a prospect" message="Prospect details and generated audits will appear here." />;

  const scores = audit?.scores || {
    designScore: company.designScore,
    seoScore: company.seoScore,
    speedScore: company.speedScore,
    mobileScore: company.mobileScore,
    leadGenerationScore: company.leadGenerationScore,
    automationScore: company.automationScore,
    overallScore: company.overallScore
  };
  const nextAction = getRevenueNextAction(company);

  return (
    <div className="revenue-detail-grid">
      <div className="revenue-detail-head">
        <div>
          <div className="row-title">
            <h2>{company.businessName}</h2>
            <GradePill grade={audit?.prospectGrade || company.prospectGrade} />
          </div>
          <p>{[company.industry, company.city].filter(Boolean).join(' / ') || 'Prospect details pending'}</p>
        </div>
        <div className="mini-actions">
          {company.website ? (
            <a className="mini-link" href={company.website} target="_blank" rel="noreferrer">
              Website <ArrowUpRight size={14} />
            </a>
          ) : null}
          <button className="btn primary" type="button" disabled={disabled || !company.website} onClick={onGenerateAudit}>
            <Sparkles size={17} />
            <span>{nextAction.label === 'Generate audit' ? 'Generate Audit' : 'Refresh Audit'}</span>
          </button>
        </div>
      </div>

      <div className="form-grid compact">
        <label>
          <span>Pipeline</span>
          <select value={company.status || 'prospect'} onChange={(event) => onStatusUpdate(event.target.value)} disabled={disabled}>
            {revenuePipelineStatuses.map((status) => <option key={status} value={status}>{titleize(status)}</option>)}
          </select>
        </label>
        <div className="data-rules">
          <p>Contact: <strong>{company.email || company.phone || 'Not recorded'}</strong></p>
          <p>Rating: <strong>{company.googleRating || 'Not recorded'}</strong></p>
        </div>
      </div>

      <RevenueDetailTabs activeTab={activeTab} onChange={setActiveTab} />

      <div className="revenue-tab-panel">
        {activeTab === 'action_plan' ? (
          <RevenueActionPlan
            audit={audit}
            company={company}
            disabled={disabled}
            nextAction={nextAction}
            onGenerateAudit={onGenerateAudit}
          />
        ) : null}

        {activeTab === 'audit' ? (
          audit ? (
            <>
              {scores?.overallScore ? <RevenueScoreGrid scores={scores} /> : null}
              <section className="audit-section compact">
                <PanelHead title="Scorecard Notes" action={`Grade ${audit.prospectGrade || company.prospectGrade || 'Pending'}`} />
                <p>{audit.notes}</p>
              </section>
              <section className="audit-lists">
                <AuditList title="Top Improvements" items={audit.topImprovements} />
                <AuditList title="Quick Wins" items={audit.quickWins} />
                <AuditList title="Automation" items={audit.automationOpportunities} />
              </section>
            </>
          ) : (
            <EmptyState title="No audit generated" message="Run Generate Audit before reviewing improvement lists." />
          )
        ) : null}

        {activeTab === 'outreach' ? (
          audit ? (
            <>
              <section className="audit-section compact">
                <PanelHead title="Loom Script" action="3 minute walkthrough" />
                <textarea className="audit-copy" value={audit.loomScript || ''} readOnly rows={5} />
              </section>

              <section className="audit-section compact">
                <PanelHead title="Outreach Drafts" action="Human approval required" />
                <div className="draft-list">
                  {(audit.outreachDrafts || []).map((draft) => (
                    <article className="draft-row" key={`${draft.step}-${draft.subject}`}>
                      <strong>Email {draft.step}: {draft.subject}</strong>
                      <p>{draft.body}</p>
                    </article>
                  ))}
                </div>
              </section>
            </>
          ) : (
            <EmptyState title="No outreach generated" message="Run Generate Audit before reviewing scripts and draft emails." />
          )
        ) : null}

        {activeTab === 'proposal' ? (
          audit ? (
            <section className="audit-section compact">
              <PanelHead
                title="Proposal Starter"
                action={(
                  <button
                    className="mini-link inline-button"
                    type="button"
                    onClick={() => {
                      if (!openPrintableAudit(company, audit)) setError('The browser blocked the print-ready audit window.');
                    }}
                  >
                    Print / PDF
                  </button>
                )}
              />
              <textarea className="audit-copy" value={audit.markdown || ''} readOnly rows={7} />
            </section>
          ) : (
            <EmptyState title="No proposal starter ready" message="Run Generate Audit before printing or exporting a scorecard." />
          )
        ) : null}

        {activeTab === 'notes' ? (
          <section className="audit-section compact">
            <PanelHead title="Internal Notes" action={titleize(company.status || 'prospect')} />
            <p>{company.notes || 'No internal notes recorded yet.'}</p>
          </section>
        ) : null}
      </div>
    </div>
  );
}

function RevenueActionPlan({ audit, company, disabled, nextAction, onGenerateAudit }) {
  const topOpportunity = audit?.topImprovements?.[0] || company.notes || 'Run an audit to surface the strongest conversion opportunity.';
  const hasContact = Boolean(company.email || company.phone);
  const hasAudit = Boolean(audit || company.latestAuditId || company.overallScore);
  const actionDisabled = disabled || (nextAction.label === 'Generate audit' && !company.website);

  return (
    <div className="revenue-action-plan">
      <section className={`revenue-next-action action-${nextAction.tone}`}>
        <div>
          <span>Next Action</span>
          <strong>{nextAction.label}</strong>
          <p>{nextAction.description}</p>
        </div>
        {nextAction.label === 'Generate audit' ? (
          <button className="btn primary" type="button" disabled={actionDisabled} onClick={onGenerateAudit}>
            <Sparkles size={17} />
            <span>Generate Audit</span>
          </button>
        ) : null}
      </section>

      <div className="revenue-action-grid">
        <div className="revenue-action-card">
          <span>Priority Reason</span>
          <strong>{getRevenuePriorityReason(company, audit)}</strong>
        </div>
        <div className="revenue-action-card">
          <span>Contact State</span>
          <strong>{hasContact ? company.email || company.phone : 'Contact missing'}</strong>
        </div>
        <div className="revenue-action-card">
          <span>Audit State</span>
          <strong>{hasAudit ? `${company.overallScore || audit?.scores?.overallScore || 0}/100` : 'Not audited'}</strong>
        </div>
      </div>

      <section className="audit-section compact">
        <PanelHead title="Top Opportunity" action={audit ? 'From audit' : 'Pending audit'} />
        <p>{topOpportunity}</p>
      </section>
    </div>
  );
}

function RevenueDetailTabs({ activeTab, onChange }) {
  return (
    <div className="revenue-detail-tabs" role="tablist" aria-label="Prospect detail sections">
      {revenueDetailTabs.map((tab) => (
        <button
          key={tab.id}
          className={activeTab === tab.id ? 'active' : ''}
          type="button"
          role="tab"
          aria-selected={activeTab === tab.id}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function RevenueScoreGrid({ scores }) {
  const items = [
    ['Overall', scores.overallScore],
    ['Design', scores.designScore],
    ['Mobile', scores.mobileScore],
    ['SEO', scores.seoScore],
    ['Lead Capture', scores.leadGenerationScore],
    ['Automation', scores.automationScore]
  ];

  return (
    <div className="score-grid">
      {items.map(([label, value]) => (
        <div className="score-tile" key={label}>
          <span>{label}</span>
          <strong>{value || 0}</strong>
        </div>
      ))}
    </div>
  );
}

function AuditList({ title, items = [] }) {
  return (
    <div className="audit-list">
      <strong>{title}</strong>
      <ul>
        {items.map((item) => <li key={item}>{item}</li>)}
      </ul>
    </div>
  );
}

function GradePill({ grade }) {
  return <span className={`grade grade-${getGradeTone(grade)}`}>{grade ? `Grade ${grade}` : 'Not graded'}</span>;
}

function getRevenuePriorityReason(company = {}, audit) {
  const grade = String(audit?.prospectGrade || company.prospectGrade || '').toUpperCase();
  const score = Number(company.overallScore || audit?.scores?.overallScore);

  if (grade.startsWith('A')) return 'High-fit local account';
  if (Number.isFinite(score) && score > 0 && score <= 55) return 'Large website opportunity';
  if (!company.latestAuditId && !audit) return 'Needs first audit';
  if (['contacted', 'audit_sent', 'proposal_sent'].includes(company.status)) return 'Follow-up motion active';
  if (company.status === 'meeting_scheduled') return 'Meeting already scheduled';
  return 'Qualified local prospect';
}

function ProjectsView({ overview }) {
  return (
    <div className="view-grid">
      <section className="panel wide">
        <PanelHead title="Projects" action={`${overview.projects.length} projects`} />
        <div className="project-list">
          {overview.projects.map((project) => (
            <article className="project-card" key={project.id}>
              <div>
                <StatusPill status={project.status} />
                <h2>{project.name}</h2>
                <p>{project.summary || 'No summary recorded.'}</p>
              </div>
              <div className="progress-track">
                <span style={{ width: `${project.progress || 0}%` }} />
              </div>
              <dl className="inline-facts">
                <div><dt>Phase</dt><dd>{project.phase || 'Queued'}</dd></div>
                <div><dt>Next</dt><dd>{project.nextMilestone || 'Review'}</dd></div>
                <div><dt>Due</dt><dd>{formatDate(project.dueDate)}</dd></div>
              </dl>
            </article>
          ))}
          {!overview.projects.length ? <EmptyState title="No projects yet" message="Your projects will appear here once we kick things off." /> : null}
        </div>
      </section>

      <section className="panel wide">
        <PanelHead title="Your website" action={`${overview.sites.length} records`} />
        <div className="table-list">
          {overview.sites.map((site) => (
            <div className="table-row" key={site.id}>
              <div>
                <strong>{site.name}</strong>
                <span>{site.domain || 'Domain not recorded'}</span>
              </div>
              <div data-label="Hosting">{site.hostingProvider || 'Hosting TBD'}</div>
              <div data-label="Ownership">{ownershipLabels[site.ownershipModel] || site.ownershipModel}</div>
            </div>
          ))}
          {!overview.sites.length ? <EmptyState title="No sites recorded" message="Site ownership and hosting records will appear here." /> : null}
        </div>
      </section>
    </div>
  );
}

function RequestsView({ overview, session, selectedClientId, refresh, setError, demo }) {
  const currentUserId = session?.user?.id;
  return (
    <div className="view-grid">
      <section className="panel">
        <PanelHead title="New request" action={overview.serviceLevel?.entitlements?.supportLabel || 'Client queue'} />
        <RequestForm
          projects={overview.projects}
          clientId={selectedClientId}
          refresh={refresh}
          setError={setError}
          demo={demo}
        />
      </section>

      <section className="panel wide">
        <PanelHead title="Your requests" action={`${overview.requests.length} total`} />
        <div className="request-board">
          {overview.requests.map((request) => (
            <RequestCard
              key={request.id}
              request={request}
              comments={commentsForRequest(overview.comments, request.id)}
              currentUserId={currentUserId}
              demo={demo}
              refresh={refresh}
              setError={setError}
            />
          ))}
          {!overview.requests.length ? <EmptyState title="No requests yet" message="Submit your first request and we'll take it from there." /> : null}
        </div>
      </section>
    </div>
  );
}

function RequestCard({ request, comments, currentUserId, demo, refresh, setError }) {
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState(comments);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setThread(comments);
  }, [comments]);

  async function handleToggle() {
    const next = !open;
    setOpen(next);
    if (next && !demo) {
      setLoading(true);
      try {
        const data = await getRequest(request.id);
        setThread(data.comments || []);
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        setLoading(false);
      }
    }
  }

  async function handleReply(event) {
    event.preventDefault();
    const body = reply.trim();
    if (!body || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      if (demo) {
        setThread((current) => [...current, {
          id: `demo-comment-${current.length + 1}`,
          requestId: request.id,
          authorUserId: currentUserId,
          authorName: 'You',
          visibility: 'client',
          body,
          createdAt: new Date().toISOString()
        }]);
        setReply('');
      } else {
        await addRequestComment(request.id, { body });
        setReply('');
        const data = await getRequest(request.id);
        setThread(data.comments || []);
        await refresh();
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  }

  const messageCount = thread.length;

  return (
    <article className={`request-card ${open ? 'open' : ''}`}>
      <button type="button" className="request-card-head" onClick={handleToggle} aria-expanded={open}>
        <span className="request-icon">
          {request.priority === 'urgent' ? <AlertTriangle size={18} /> : <Clock3 size={18} />}
        </span>
        <span className="request-card-main">
          <span className="row-title">
            <strong>{request.title}</strong>
            <StatusPill status={request.status} />
          </span>
          <span className="request-card-sub">{request.projectName || 'General'} - {request.pageArea || titleize(request.requestType)}</span>
          <small>{formatDate(request.createdAt)} / {titleize(request.priority)} priority{messageCount ? ` / ${messageCount} ${messageCount === 1 ? 'message' : 'messages'}` : ''}</small>
        </span>
        <ChevronDown className="request-card-chevron" size={18} aria-hidden="true" />
      </button>

      {open ? (
        <div className="request-thread">
          {request.details ? <p className="request-details">{request.details}</p> : null}
          {loading ? <p className="thread-status">Loading conversation…</p> : null}
          <div className="conversation">
            {messageCount ? thread.map((comment) => (
              <CommentBubble key={comment.id} comment={comment} mine={isOwnComment(comment, currentUserId)} />
            )) : (!loading ? <EmptyState title="No messages yet" message="Add a reply below to start the conversation with JJE." /> : null)}
          </div>
          <form className="reply-form" onSubmit={handleReply}>
            <label className="sr-only" htmlFor={`reply-${request.id}`}>Reply to this request</label>
            <textarea
              id={`reply-${request.id}`}
              value={reply}
              onChange={(event) => setReply(event.target.value)}
              rows={3}
              placeholder="Write a reply to JJE…"
            />
            <button className="btn primary" type="submit" disabled={submitting || !reply.trim()}>
              <Send size={16} />
              <span>{submitting ? 'Sending' : 'Send reply'}</span>
            </button>
          </form>
        </div>
      ) : null}
    </article>
  );
}

function RequestForm({ projects, clientId, refresh, setError, demo }) {
  const [form, setForm] = useState({
    title: '',
    projectId: '',
    pageArea: '',
    requestType: 'content',
    priority: 'normal',
    duePreference: '',
    details: ''
  });
  const [submitting, setSubmitting] = useState(false);

  function update(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      if (!demo) {
        await createChangeRequest({ ...form, clientId, projectId: form.projectId || null });
      }
      setForm({ title: '', projectId: '', pageArea: '', requestType: 'content', priority: 'normal', duePreference: '', details: '' });
      await refresh('Request submitted.');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="form-stack" onSubmit={handleSubmit}>
      <label>
        <span>What do you need changed?</span>
        <input value={form.title} onChange={(event) => update('title', event.target.value)} placeholder="Update homepage headline, add a new service, fix a form issue" required />
      </label>
      <label>
        <span>Project</span>
        <select value={form.projectId} onChange={(event) => update('projectId', event.target.value)}>
          <option value="">General website request</option>
          {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
        </select>
      </label>
      <label>
        <span>Details or context</span>
        <textarea value={form.details} onChange={(event) => update('details', event.target.value)} rows={6} placeholder="Describe the change, where it should happen, links, files, timing, or anything JJE should know." required />
      </label>
      <div className="form-grid">
        <label>
          <span>Type</span>
          <select value={form.requestType} onChange={(event) => update('requestType', event.target.value)}>
            {requestTypes.map((type) => <option key={type} value={type}>{titleize(type)}</option>)}
          </select>
        </label>
        <label>
          <span>Priority</span>
          <select value={form.priority} onChange={(event) => update('priority', event.target.value)}>
            {priorities.map((priority) => <option key={priority} value={priority}>{titleize(priority)}</option>)}
          </select>
        </label>
      </div>
      <label>
        <span>Page or area</span>
        <input value={form.pageArea} onChange={(event) => update('pageArea', event.target.value)} placeholder="Homepage, services, footer" />
      </label>
      <label>
        <span>Timing</span>
        <input value={form.duePreference} onChange={(event) => update('duePreference', event.target.value)} placeholder="This week, before launch, no rush" />
      </label>
      <button className="btn primary" type="submit" disabled={submitting}>
        <Plus size={17} />
        <span>{submitting ? 'Creating' : 'Create Request'}</span>
      </button>
    </form>
  );
}

function FilesView({ overview, selectedClientId, refresh, setError, demo }) {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState('');
  const [requestId, setRequestId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!file) return;
    setSubmitting(true);
    setError('');
    try {
      if (!demo) {
        const formData = new FormData();
        formData.append('clientId', selectedClientId);
        formData.append('file', file);
        formData.append('note', note);
        if (requestId) formData.append('requestId', requestId);
        await uploadClientFile(formData);
      }
      setFile(null);
      setNote('');
      setRequestId('');
      event.currentTarget.reset();
      await refresh('File uploaded.');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="view-grid">
      <section className="panel">
        <PanelHead title="Upload File" action="10 MB max" />
        <form className="form-stack" onSubmit={handleSubmit}>
          <label className="upload-zone">
            <UploadCloud size={28} />
            <span>{file ? file.name : 'Choose a file'}</span>
            <input type="file" onChange={(event) => setFile(event.target.files?.[0] || null)} />
          </label>
          <label>
            <span>Attach to request</span>
            <select value={requestId} onChange={(event) => setRequestId(event.target.value)}>
              <option value="">General client file</option>
              {overview.requests.map((request) => <option key={request.id} value={request.id}>{request.title}</option>)}
            </select>
          </label>
          <label>
            <span>Note</span>
            <textarea value={note} onChange={(event) => setNote(event.target.value)} rows={4} />
          </label>
          <button className="btn primary" type="submit" disabled={!file || submitting}>
            <FileUp size={17} />
            <span>{submitting ? 'Uploading' : 'Upload File'}</span>
          </button>
        </form>
      </section>

      <section className="panel wide">
        <PanelHead title="Files" action={`${overview.files.length} active`} />
        <div className="table-list">
          {overview.files.map((item) => (
            <a className="table-row file-row" href={`/api/files/${encodeURIComponent(item.id)}/download`} key={item.id} aria-label={`Download ${item.originalFilename}`}>
              <div>
                <strong>{item.originalFilename}</strong>
                <span>{item.note || 'No note'}</span>
              </div>
              <div data-label="Size">{formatBytes(item.size)}</div>
              <div data-label="Uploaded">{formatDate(item.createdAt)}</div>
              <ArrowUpRight size={16} aria-hidden="true" />
            </a>
          ))}
          {!overview.files.length ? <EmptyState title="No files yet" message="Files you upload and assets we deliver will appear here." /> : null}
        </div>
      </section>
    </div>
  );
}

function BillingView({ overview, selectedClientId, setNotice, setError, demo }) {
  return (
    <div className="view-grid">
      <section className="panel">
        <PanelHead title="Billing" action="Secure payments" />
        <BillingSummary overview={overview} selectedClientId={selectedClientId} setNotice={setNotice} setError={setError} demo={demo} />
      </section>
      {overview.serviceLevel ? (
        <section className="panel">
          <PanelHead title="Service Level" action={titleize(overview.serviceLevel.status || 'active')} />
          <MiniServiceSummary serviceLevel={overview.serviceLevel} />
        </section>
      ) : null}
      <section className="panel wide">
        <PanelHead title="Invoices" action={`${overview.billing.length} records`} />
        <div className="table-list">
          {overview.billing.map((item) => (
            <div className="table-row" key={item.id}>
              <div>
                <strong>{item.description}</strong>
                <span>{titleize(item.kind)} - {titleize(item.status)}</span>
              </div>
              <div data-label="Amount">{formatMoney(item.amountCents, item.currency)}</div>
              <div data-label="Due">{formatDate(item.dueAt || item.createdAt)}</div>
              <div>
                {item.hostedInvoiceUrl ? (
                  <a className="mini-link" href={item.hostedInvoiceUrl} target="_blank" rel="noreferrer">Invoice</a>
                ) : 'Portal'}
              </div>
            </div>
          ))}
          {!overview.billing.length ? <EmptyState title="No billing records" message="Invoices and subscriptions will appear here." /> : null}
        </div>
      </section>
    </div>
  );
}

function MiniServiceSummary({ serviceLevel }) {
  return (
    <div className="mini-service-summary">
      <h2>{serviceLevel.name}</h2>
      <p>{serviceLevel.summary}</p>
      <dl className="inline-facts">
        <div><dt>Response</dt><dd>{serviceLevel.responseTarget}</dd></div>
        <div><dt>Requests</dt><dd>{serviceLevel.entitlements?.requestLimitLabel || `${serviceLevel.monthlyRequestAllowance} / month`}</dd></div>
        <div><dt>Planning</dt><dd>{serviceLevel.entitlements?.planningCadence || 'As needed'}</dd></div>
      </dl>
    </div>
  );
}

function BillingSummary({ overview, selectedClientId, setNotice, setError, demo }) {
  const openItems = overview.billing.filter((item) => !['paid', 'active'].includes(item.status));
  const stripeLinked = Boolean(overview.client?.stripeCustomerId);

  async function handlePortal() {
    try {
      if (demo) {
        setNotice('Stripe portal would open in production.');
        return;
      }
      if (!stripeLinked) {
        setError('This client is not linked to a Stripe customer yet.');
        return;
      }
      const result = await openStripePortal(selectedClientId);
      window.location.href = result.url;
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  return (
    <div className="billing-summary">
      <div className="billing-total">
        <span>Open balance</span>
        <strong>{formatMoney(openItems.reduce((sum, item) => sum + Number(item.amountCents || 0), 0), 'usd')}</strong>
      </div>
      <p>Payment method updates, invoices, and subscription changes open in Stripe.</p>
      {!stripeLinked && !demo ? (
        <p className="setup-hint">Stripe customer setup is needed before this button opens.</p>
      ) : null}
      <button className="btn primary" type="button" onClick={handlePortal} disabled={!stripeLinked && !demo}>
        <CreditCard size={17} />
        <span>Open Stripe Portal</span>
      </button>
    </div>
  );
}

function AccountView({ session, overview }) {
  return (
    <div className="view-grid">
      <section className="panel">
        <PanelHead title="Profile" action={session.isAdmin ? 'JJE staff' : 'Client'} />
        <div className="profile-block">
          <span className="profile-avatar">{session.user.avatarInitials || getInitials(session.user.name, session.user.email)}</span>
          <h2>{session.user.name}</h2>
          <p>{session.user.email}</p>
        </div>
      </section>
      <section className="panel wide">
        <PanelHead title="Client Access" action={`${session.memberships.length} memberships`} />
        <div className="table-list">
          {session.memberships.map((membership) => (
            <div className="table-row" key={membership.id}>
              <div>
                <strong>{membership.clientName}</strong>
                <span>{membership.clientSlug}</span>
              </div>
              <div data-label="Role">{titleize(membership.role)}</div>
              <div data-label="Membership">{titleize(membership.status)}</div>
              <div data-label="Workspace">{titleize(membership.clientStatus)}</div>
            </div>
          ))}
        </div>
      </section>
      <section className="panel wide">
        <PanelHead title="Your privacy & access" action="Private" />
        <div className="data-rules">
          <p>No passwords, registrar credentials, GitHub tokens, Cloudflare tokens, or email logins are stored in this portal.</p>
          <p>Payments are handled securely by Stripe — your card details never touch this portal.</p>
        </div>
      </section>
    </div>
  );
}

const adminSections = [
  { id: 'onboarding', label: 'Onboarding', icon: Sparkles },
  { id: 'service', label: 'Service', icon: CheckCircle2 },
  { id: 'workspace', label: 'Workspace', icon: FolderKanban },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'audit', label: 'Audit', icon: ShieldCheck }
];

function AdminView({ overview, selectedClientId, setSelectedClientId, refresh, setNotice, setError, demo }) {
  const [section, setSection] = useState('onboarding');

  return (
    <div className="admin-stack">
      <nav className="admin-tabs" aria-label="Admin sections">
        {adminSections.map(({ id, label, icon: Icon }) => (
          <button
            className={section === id ? 'active' : ''}
            key={id}
            type="button"
            onClick={() => setSection(id)}
          >
            <Icon size={17} />
            <span>{label}</span>
          </button>
        ))}
      </nav>

      <section className="panel wide admin-overview">
        <PanelHead title="Workspace Map" action={overview.client?.name || 'Active client'} />
        <div className="admin-map">
          <div className="admin-map-root">
            <span>Client</span>
            <strong>{overview.client?.name || 'Workspace'}</strong>
          </div>
          <div className="admin-map-grid">
            <AdminMapItem label="Projects" value={overview.projects.length} />
            <AdminMapItem label="Sites" value={overview.sites.length} />
            <AdminMapItem label="Requests" value={overview.requests.length} />
            <AdminMapItem label="Files" value={overview.files.length} />
            <AdminMapItem label="Billing" value={overview.billing.length} />
            <AdminMapItem label="Service" value={overview.serviceLevel?.shortName || 'None'} />
          </div>
        </div>
      </section>

      {section === 'onboarding' ? (
        <div className="view-grid">
          <section className="panel wide">
            <PanelHead title="Automated Onboarding" action="Recommended" />
            <OnboardingForm setSelectedClientId={setSelectedClientId} setNotice={setNotice} setError={setError} demo={demo} />
          </section>
          <section className="panel">
            <PanelHead title="Invite User" action="Existing client" />
            <InviteForm clients={overview.clients} selectedClientId={selectedClientId} refresh={refresh} setError={setError} demo={demo} />
          </section>
        </div>
      ) : null}

      {section === 'service' ? (
        <div className="view-grid">
          <section className="panel">
            <PanelHead title="Client Service Level" action={overview.serviceLevel?.name || 'Foundation Care'} />
            <ServiceLevelForm
              clientId={selectedClientId}
              serviceLevel={overview.serviceLevel}
              servicePackages={overview.servicePackages}
              refresh={refresh}
              setNotice={setNotice}
              setError={setError}
              demo={demo}
            />
          </section>
          <section className="panel wide">
            <PanelHead title="Available Packages" action="Activation menu" />
            <ServicePackageList servicePackages={overview.servicePackages} activePackageKey={overview.serviceLevel?.packageKey} />
          </section>
        </div>
      ) : null}

      {section === 'workspace' ? (
        <div className="view-grid">
          <section className="panel">
            <PanelHead title="Create Client" action="Manual" />
            <AdminClientForm refresh={refresh} setError={setError} demo={demo} />
          </section>
          <section className="panel">
            <PanelHead title="Add Project" action="Selected client" />
            <AdminProjectForm clientId={selectedClientId} refresh={refresh} setError={setError} demo={demo} />
          </section>
          <section className="panel">
            <PanelHead title="Add Site Record" action="Selected client" />
            <AdminSiteForm projects={overview.projects} clientId={selectedClientId} refresh={refresh} setError={setError} demo={demo} />
          </section>
        </div>
      ) : null}

      {section === 'billing' ? (
        <div className="view-grid">
          <section className="panel">
            <PanelHead title="Billing Record" action="Stripe-hosted" />
            <AdminBillingForm clientId={selectedClientId} refresh={refresh} setError={setError} demo={demo} />
          </section>
        </div>
      ) : null}

      {section === 'audit' ? (
        <section className="panel wide">
          <PanelHead title="Audit History" action={`${overview.auditEvents.length} events`} />
          <div className="table-list">
            {overview.auditEvents.map((event) => (
              <div className="table-row" key={event.id}>
                <div>
                  <strong>{titleize(event.eventType)}</strong>
                  <span>{event.actorName || 'System'}</span>
                </div>
                <div>{event.entityType || 'portal'}</div>
                <div>{formatDate(event.createdAt)}</div>
              </div>
            ))}
            {!overview.auditEvents.length ? <EmptyState title="No audit events" message="Security-sensitive activity will appear here." /> : null}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function AdminMapItem({ label, value }) {
  return (
    <div className="admin-map-item">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ServiceLevelForm({ clientId, serviceLevel, servicePackages = [], refresh, setNotice, setError, demo }) {
  const packages = servicePackages.length ? servicePackages : [serviceLevel].filter(Boolean);
  const [form, setForm] = useState(() => serviceFormFromLevel(serviceLevel));

  useEffect(() => {
    setForm(serviceFormFromLevel(serviceLevel));
  }, [serviceLevel?.enrollmentId, serviceLevel?.packageKey, serviceLevel?.status]);

  return (
    <AdminForm
      submitLabel="Update Service"
      icon={CheckCircle2}
      onSubmit={async () => {
        if (!demo) await updateClientServiceLevel({ ...form, clientId });
        setNotice('Service level updated.');
        await refresh();
      }}
      setError={setError}
    >
      <label>
        <span>Service package</span>
        <select value={form.packageKey} onChange={(event) => setForm({ ...form, packageKey: event.target.value })}>
          {packages.map((item) => (
            <option key={item.key || item.packageKey} value={item.key || item.packageKey}>{item.name}</option>
          ))}
        </select>
      </label>
      <Select label="Status" value={form.status} onChange={(value) => setForm({ ...form, status: value })} options={serviceStatuses} />
      <Input label="Stripe subscription ID" value={form.stripeSubscriptionId} onChange={(value) => setForm({ ...form, stripeSubscriptionId: value })} />
      <Input label="Started at" type="date" value={form.startedAt} onChange={(value) => setForm({ ...form, startedAt: value })} />
      <Input label="Ends at" type="date" value={form.endsAt} onChange={(value) => setForm({ ...form, endsAt: value })} />
      <Textarea label="Internal notes" value={form.internalNotes} onChange={(value) => setForm({ ...form, internalNotes: value })} />
    </AdminForm>
  );
}

function ServicePackageList({ servicePackages = [], activePackageKey }) {
  return (
    <div className="service-package-list">
      {servicePackages.map((servicePackage) => (
        <article className={servicePackage.key === activePackageKey ? 'service-package-card active' : 'service-package-card'} key={servicePackage.key}>
          <div>
            <span>{servicePackage.shortName}</span>
            <h3>{servicePackage.name}</h3>
            <p>{servicePackage.summary}</p>
          </div>
          <dl className="inline-facts">
            <div><dt>Response</dt><dd>{servicePackage.responseTarget}</dd></div>
            <div><dt>Requests</dt><dd>{servicePackage.entitlements?.requestLimitLabel || `${servicePackage.monthlyRequestAllowance} / month`}</dd></div>
            <div><dt>Planning</dt><dd>{servicePackage.entitlements?.planningCadence || 'As needed'}</dd></div>
          </dl>
        </article>
      ))}
    </div>
  );
}

function serviceFormFromLevel(serviceLevel = {}) {
  return {
    packageKey: serviceLevel.packageKey || 'foundation_care',
    status: serviceLevel.status || 'active',
    stripeSubscriptionId: serviceLevel.stripeSubscriptionId || '',
    startedAt: dateInputValue(serviceLevel.startedAt) || dateInputValue(new Date().toISOString()),
    endsAt: dateInputValue(serviceLevel.endsAt),
    internalNotes: serviceLevel.internalNotes || ''
  };
}

function OnboardingForm({ setSelectedClientId, setNotice, setError, demo }) {
  const initialForm = {
    clientName: '',
    ownerName: '',
    ownerEmail: '',
    billingEmail: '',
    domain: '',
    ownershipModel: 'hybrid',
    servicePackageKey: 'foundation_care',
    serviceStatus: 'active',
    serviceNotes: '',
    passwordManagerRef: '',
    projectName: '',
    createStripeCustomer: true,
    sendInvite: true,
    createStarterProject: true,
    createSiteRecord: true,
    createIntroRequest: true
  };
  const [form, setForm] = useState(initialForm);

  return (
    <AdminForm
      submitLabel="Onboard Client"
      icon={Sparkles}
      onSubmit={async () => {
        const result = demo
          ? { clientId: 'demo-client', warnings: [] }
          : await onboardClient(form);

        setForm(initialForm);
        if (result.clientId) setSelectedClientId(result.clientId);
        const warningText = result.warnings?.length ? ` Warnings: ${result.warnings.join(' ')}` : '';
        setNotice(`Client onboarding is set up.${warningText}`);
      }}
      setError={setError}
    >
      <div className="form-grid">
        <Input label="Client name" value={form.clientName} onChange={(value) => setForm({ ...form, clientName: value })} required />
        <Input label="Website domain" value={form.domain} onChange={(value) => setForm({ ...form, domain: value })} />
        <Input label="Owner name" value={form.ownerName} onChange={(value) => setForm({ ...form, ownerName: value })} required />
        <Input label="Owner email" type="email" value={form.ownerEmail} onChange={(value) => setForm({ ...form, ownerEmail: value })} required />
        <Input label="Billing email" type="email" value={form.billingEmail} onChange={(value) => setForm({ ...form, billingEmail: value })} />
        <Select label="Ownership" value={form.ownershipModel} onChange={(value) => setForm({ ...form, ownershipModel: value })} options={['hybrid', 'client_owned', 'jje_managed']} />
        <ServicePackageSelect label="Service level" value={form.servicePackageKey} onChange={(value) => setForm({ ...form, servicePackageKey: value })} />
        <Select label="Service status" value={form.serviceStatus} onChange={(value) => setForm({ ...form, serviceStatus: value })} options={serviceStatuses} />
        <Input label="Starter project name" value={form.projectName} onChange={(value) => setForm({ ...form, projectName: value })} />
        <Input label="Password manager ref" value={form.passwordManagerRef} onChange={(value) => setForm({ ...form, passwordManagerRef: value })} />
        <Textarea label="Service notes" value={form.serviceNotes} onChange={(value) => setForm({ ...form, serviceNotes: value })} />
      </div>
      <div className="option-grid">
        <Checkbox label="Create Stripe customer" checked={form.createStripeCustomer} onChange={(value) => setForm({ ...form, createStripeCustomer: value })} />
        <Checkbox label="Send owner invite" checked={form.sendInvite} onChange={(value) => setForm({ ...form, sendInvite: value })} />
        <Checkbox label="Open starter project" checked={form.createStarterProject} onChange={(value) => setForm({ ...form, createStarterProject: value })} />
        <Checkbox label="Create site record" checked={form.createSiteRecord} onChange={(value) => setForm({ ...form, createSiteRecord: value })} />
        <Checkbox label="Create onboarding request" checked={form.createIntroRequest} onChange={(value) => setForm({ ...form, createIntroRequest: value })} />
      </div>
    </AdminForm>
  );
}

function AdminClientForm({ refresh, setError, demo }) {
  const initialForm = {
    name: '',
    billingEmail: '',
    preferredOwnershipModel: 'hybrid',
    servicePackageKey: 'foundation_care',
    serviceStatus: 'active',
    stripeCustomerId: '',
    stripeSubscriptionId: '',
    passwordManagerRef: '',
    serviceNotes: ''
  };
  const [form, setForm] = useState(initialForm);
  return (
    <AdminForm
      submitLabel="Create Client"
      onSubmit={async () => {
        const result = demo ? { warnings: [] } : await createClient(form);
        setForm(initialForm);
        if (result?.warnings?.length) setError(result.warnings.join(' '));
        await refresh('Client created.');
      }}
      setError={setError}
    >
      <Input label="Name" value={form.name} onChange={(value) => setForm({ ...form, name: value })} required />
      <Input label="Billing email" value={form.billingEmail} onChange={(value) => setForm({ ...form, billingEmail: value })} />
      <Select label="Ownership" value={form.preferredOwnershipModel} onChange={(value) => setForm({ ...form, preferredOwnershipModel: value })} options={['hybrid', 'client_owned', 'jje_managed']} />
      <ServicePackageSelect label="Service level" value={form.servicePackageKey} onChange={(value) => setForm({ ...form, servicePackageKey: value })} />
      <Select label="Service status" value={form.serviceStatus} onChange={(value) => setForm({ ...form, serviceStatus: value })} options={serviceStatuses} />
      <Input label="Stripe customer ID" value={form.stripeCustomerId} onChange={(value) => setForm({ ...form, stripeCustomerId: value })} />
      <Input label="Stripe subscription ID" value={form.stripeSubscriptionId} onChange={(value) => setForm({ ...form, stripeSubscriptionId: value })} />
      <Input label="Password manager ref" value={form.passwordManagerRef} onChange={(value) => setForm({ ...form, passwordManagerRef: value })} />
      <Textarea label="Service notes" value={form.serviceNotes} onChange={(value) => setForm({ ...form, serviceNotes: value })} />
    </AdminForm>
  );
}

function InviteForm({ clients, selectedClientId, refresh, setError, demo }) {
  const [form, setForm] = useState({ clientId: selectedClientId, name: '', email: '', role: 'client_owner' });
  useEffect(() => setForm((current) => ({ ...current, clientId: selectedClientId })), [selectedClientId]);
  return (
    <AdminForm
      submitLabel="Send Invite"
      onSubmit={async () => {
        if (!demo) await inviteUser(form);
        setForm({ clientId: selectedClientId, name: '', email: '', role: 'client_owner' });
        await refresh('Invite sent.');
      }}
      setError={setError}
    >
      <label>
        <span>Client</span>
        <select value={form.clientId} onChange={(event) => setForm({ ...form, clientId: event.target.value })}>
          {clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
        </select>
      </label>
      <Input label="Name" value={form.name} onChange={(value) => setForm({ ...form, name: value })} required />
      <Input label="Email" type="email" value={form.email} onChange={(value) => setForm({ ...form, email: value })} required />
      <Select label="Role" value={form.role} onChange={(value) => setForm({ ...form, role: value })} options={['client_owner', 'client_member', 'jje_staff', 'jje_admin']} />
      {['jje_staff', 'jje_admin'].includes(form.role) ? (
        <p className="notice danger">Heads up: JJE staff/admin can see every client, plus all Bottleneck leads and Revenue data — not just this workspace. Use Client owner or Client member for clients.</p>
      ) : null}
    </AdminForm>
  );
}

function AdminProjectForm({ clientId, refresh, setError, demo }) {
  const [form, setForm] = useState({ name: '', summary: '', phase: '', progress: 0, nextMilestone: '', dueDate: '' });
  return (
    <AdminForm
      submitLabel="Add Project"
      onSubmit={async () => {
        if (!demo) await createProject({ ...form, clientId });
        setForm({ name: '', summary: '', phase: '', progress: 0, nextMilestone: '', dueDate: '' });
        await refresh('Project added.');
      }}
      setError={setError}
    >
      <Input label="Name" value={form.name} onChange={(value) => setForm({ ...form, name: value })} required />
      <Textarea label="Summary" value={form.summary} onChange={(value) => setForm({ ...form, summary: value })} />
      <Input label="Phase" value={form.phase} onChange={(value) => setForm({ ...form, phase: value })} />
      <Input label="Progress" type="number" value={form.progress} onChange={(value) => setForm({ ...form, progress: value })} />
      <Input label="Next milestone" value={form.nextMilestone} onChange={(value) => setForm({ ...form, nextMilestone: value })} />
      <Input label="Due date" type="date" value={form.dueDate} onChange={(value) => setForm({ ...form, dueDate: value })} />
    </AdminForm>
  );
}

function AdminSiteForm({ projects, clientId, refresh, setError, demo }) {
  const [form, setForm] = useState({ name: '', domain: '', projectId: '', ownershipModel: 'hybrid', hostingProvider: '', githubRepoRef: '', cloudflareAccountRef: '', passwordManagerRef: '', offboardingNotes: '' });
  return (
    <AdminForm
      submitLabel="Add Site"
      onSubmit={async () => {
        if (!demo) await createSite({ ...form, clientId });
        setForm({ name: '', domain: '', projectId: '', ownershipModel: 'hybrid', hostingProvider: '', githubRepoRef: '', cloudflareAccountRef: '', passwordManagerRef: '', offboardingNotes: '' });
        await refresh('Site record added.');
      }}
      setError={setError}
    >
      <Input label="Name" value={form.name} onChange={(value) => setForm({ ...form, name: value })} required />
      <Input label="Domain" value={form.domain} onChange={(value) => setForm({ ...form, domain: value })} />
      <label>
        <span>Project</span>
        <select value={form.projectId} onChange={(event) => setForm({ ...form, projectId: event.target.value })}>
          <option value="">No project</option>
          {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
        </select>
      </label>
      <Select label="Ownership" value={form.ownershipModel} onChange={(value) => setForm({ ...form, ownershipModel: value })} options={['hybrid', 'client_owned', 'jje_managed']} />
      <Input label="Hosting provider" value={form.hostingProvider} onChange={(value) => setForm({ ...form, hostingProvider: value })} />
      <Input label="GitHub repo ref" value={form.githubRepoRef} onChange={(value) => setForm({ ...form, githubRepoRef: value })} />
      <Input label="Cloudflare account ref" value={form.cloudflareAccountRef} onChange={(value) => setForm({ ...form, cloudflareAccountRef: value })} />
      <Input label="Password manager ref" value={form.passwordManagerRef} onChange={(value) => setForm({ ...form, passwordManagerRef: value })} />
      <Textarea label="Offboarding notes" value={form.offboardingNotes} onChange={(value) => setForm({ ...form, offboardingNotes: value })} />
    </AdminForm>
  );
}

function AdminBillingForm({ clientId, refresh, setError, demo }) {
  const [form, setForm] = useState({ description: '', kind: 'invoice', amountCents: '', status: 'open', hostedInvoiceUrl: '', stripeInvoiceId: '', stripeSubscriptionId: '' });
  return (
    <AdminForm
      submitLabel="Add Billing"
      onSubmit={async () => {
        if (!demo) await createBillingRecord({ ...form, clientId });
        setForm({ description: '', kind: 'invoice', amountCents: '', status: 'open', hostedInvoiceUrl: '', stripeInvoiceId: '', stripeSubscriptionId: '' });
        await refresh('Billing record added.');
      }}
      setError={setError}
    >
      <Input label="Description" value={form.description} onChange={(value) => setForm({ ...form, description: value })} required />
      <Select label="Kind" value={form.kind} onChange={(value) => setForm({ ...form, kind: value })} options={['invoice', 'subscription', 'retainer', 'credit', 'other']} />
      <Input label="Amount cents" type="number" value={form.amountCents} onChange={(value) => setForm({ ...form, amountCents: value })} />
      <Input label="Status" value={form.status} onChange={(value) => setForm({ ...form, status: value })} />
      <Input label="Hosted invoice URL" value={form.hostedInvoiceUrl} onChange={(value) => setForm({ ...form, hostedInvoiceUrl: value })} />
      <Input label="Stripe invoice ID" value={form.stripeInvoiceId} onChange={(value) => setForm({ ...form, stripeInvoiceId: value })} />
      <Input label="Stripe subscription ID" value={form.stripeSubscriptionId} onChange={(value) => setForm({ ...form, stripeSubscriptionId: value })} />
    </AdminForm>
  );
}

function AdminForm({ children, submitLabel, onSubmit, setError, icon: Icon = Plus }) {
  const [submitting, setSubmitting] = useState(false);
  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await onSubmit();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="form-stack compact" onSubmit={handleSubmit}>
      {children}
      <button className="btn primary" type="submit" disabled={submitting}>
        <Icon size={17} />
        <span>{submitting ? 'Saving' : submitLabel}</span>
      </button>
    </form>
  );
}

function Input({ label, value, onChange, type = 'text', required = false }) {
  return (
    <label>
      <span>{label}</span>
      <input type={type} value={value} onChange={(event) => onChange(event.target.value)} required={required} />
    </label>
  );
}

function Textarea({ label, value, onChange }) {
  return (
    <label>
      <span>{label}</span>
      <textarea value={value} onChange={(event) => onChange(event.target.value)} rows={3} />
    </label>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label>
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => <option key={option} value={option}>{titleize(option)}</option>)}
      </select>
    </label>
  );
}

function ServicePackageSelect({ label, value, onChange }) {
  return (
    <label>
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {servicePackageOptions.map((option) => <option key={option.key} value={option.key}>{option.name}</option>)}
      </select>
    </label>
  );
}

function Checkbox({ label, checked, onChange }) {
  return (
    <label className="check-option">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function Metric({ label, value, icon: Icon }) {
  return (
    <article className="metric">
      <Icon size={20} aria-hidden="true" />
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function PanelHead({ title, action }) {
  return (
    <div className="panel-head">
      <h2>{title}</h2>
      {action ? (
        typeof action === 'string' ? <span>{action}</span> : <div className="panel-action">{action}</div>
      ) : null}
    </div>
  );
}

function RequestRow({ request, expanded = false }) {
  return (
    <article className={`request-row ${expanded ? 'expanded' : ''}`}>
      <div className="request-icon">
        {request.priority === 'urgent' ? <AlertTriangle size={18} /> : <Clock3 size={18} />}
      </div>
      <div>
        <div className="row-title">
          <strong>{request.title}</strong>
          <StatusPill status={request.status} />
        </div>
        <p>{expanded ? request.details : `${request.projectName || 'General'} - ${request.pageArea || titleize(request.requestType)}`}</p>
        <small>{formatDate(request.createdAt)} / {titleize(request.priority)} priority</small>
      </div>
    </article>
  );
}

function CommentBubble({ comment, mine = false }) {
  return (
    <article className={`comment-bubble ${mine ? 'mine' : ''}`}>
      <div>
        <strong>{mine ? 'You' : comment.authorName || 'JJE Creative'}</strong>
        <span>{formatDate(comment.createdAt)}</span>
      </div>
      <p>{comment.body}</p>
    </article>
  );
}

function CheckItem({ done, label }) {
  return (
    <div className={done ? 'check-item done' : 'check-item'}>
      <CheckCircle2 size={18} />
      <span>{label}</span>
    </div>
  );
}

function EmptyState({ title, message, action }) {
  return (
    <div className="empty-state">
      <strong>{title}</strong>
      <span>{message}</span>
      {action ? (
        <button type="button" className="btn primary" onClick={action.onClick}>
          <span>{action.label}</span>
        </button>
      ) : null}
    </div>
  );
}

function StatusPill({ status }) {
  return <span className={`status ${String(status || '').replace(/_/g, '-')}`}>{titleize(status || 'queued')}</span>;
}

function DismissibleNotice({ tone, message, onDismiss }) {
  return (
    <div className={`notice ${tone}`}>
      <span>{message}</span>
      <button type="button" onClick={onDismiss}>Dismiss</button>
    </div>
  );
}

function parseProspectImport(text) {
  const rows = String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map(splitDelimitedRow);

  if (!rows.length) return [];

  const firstRow = rows[0].map((cell) => cell.toLowerCase());
  const hasHeader = firstRow.some((cell) => ['business', 'business name', 'company', 'website', 'industry'].includes(cell));
  const headers = hasHeader ? firstRow : ['businessName', 'website', 'industry', 'city', 'email', 'phone', 'googleRating', 'notes'];
  const dataRows = hasHeader ? rows.slice(1) : rows;

  return dataRows.map((row) => {
    const record = {};
    headers.forEach((header, index) => {
      const key = normalizeImportHeader(header);
      if (key) record[key] = row[index] || '';
    });
    return {
      businessName: record.businessName || row[0] || '',
      website: record.website || row[1] || '',
      industry: record.industry || row[2] || 'Professional Services',
      city: record.city || row[3] || 'Franklin',
      email: record.email || row[4] || '',
      phone: record.phone || row[5] || '',
      googleRating: record.googleRating || row[6] || '',
      notes: record.notes || row[7] || ''
    };
  }).filter((company) => company.businessName);
}

function splitDelimitedRow(line) {
  const delimiter = line.includes('\t') ? '\t' : ',';
  const cells = [];
  let current = '';
  let quoted = false;

  for (const char of line) {
    if (char === '"') {
      quoted = !quoted;
    } else if (char === delimiter && !quoted) {
      cells.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  cells.push(current.trim());
  return cells.map((cell) => cell.replace(/^"|"$/g, '').trim());
}

function normalizeImportHeader(header) {
  const clean = String(header || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  const map = {
    business: 'businessName',
    businessname: 'businessName',
    company: 'businessName',
    companyname: 'businessName',
    website: 'website',
    domain: 'website',
    industry: 'industry',
    city: 'city',
    email: 'email',
    phone: 'phone',
    googlerating: 'googleRating',
    rating: 'googleRating',
    notes: 'notes'
  };
  return map[clean] || '';
}

function buildDemoDiscoveryPlan(cities, industries) {
  const tasks = [];
  for (const city of cities) {
    for (const industry of industries) {
      const query = `${industry} in ${city} TN independent business website`;
      tasks.push({
        id: `${city}-${industry}`,
        city,
        industry,
        query,
        searchUrl: `https://www.google.com/search?q=${encodeURIComponent(query)}`,
        mapsUrl: `https://www.google.com/maps/search/${encodeURIComponent(`${industry} ${city} TN`)}`
      });
    }
  }
  return { cities, industries, tasks };
}

function openPrintableAudit(company, audit) {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return false;

  const markdown = escapeHtml(audit.markdown || '');
  printWindow.document.write(`
    <!doctype html>
    <html>
      <head>
        <title>${escapeHtml(company.businessName)} Website Scorecard</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <style>
          body { font-family: Inter, Arial, sans-serif; margin: 40px; color: #25221f; line-height: 1.55; }
          h1 { font-size: 32px; margin-bottom: 8px; }
          pre { white-space: pre-wrap; font: inherit; }
          .brand { color: #6f725e; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; font-size: 12px; }
          @media print { body { margin: 24px; } button { display: none; } }
        </style>
      </head>
      <body>
        <p class="brand">JJE Creative / Websites That Feel Alive</p>
        <h1>${escapeHtml(company.businessName)} Website Scorecard</h1>
        <pre>${markdown}</pre>
        <button onclick="window.print()">Print or Save PDF</button>
      </body>
    </html>
  `);
  printWindow.document.close();
  return true;
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function getStats(overview) {
  return {
    openRequests: overview.requests.filter((request) => !['completed', 'canceled'].includes(request.status)).length,
    activeProjects: overview.projects.filter((project) => ['planning', 'active', 'waiting'].includes(project.status)).length,
    files: overview.files.length,
    billingOpen: overview.billing.filter((item) => !['paid', 'active'].includes(item.status)).length
  };
}

function titleize(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDate(value) {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
}

function formatDateTime(value) {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
}

function dateInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function formatMoney(cents, currency = 'usd') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: String(currency || 'usd').toUpperCase()
  }).format(Number(cents || 0) / 100);
}

function formatBytes(bytes) {
  const value = Number(bytes || 0);
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function getInitials(name, email) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return `${words[0][0]}${words[1][0]}`.toUpperCase();
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return String(email || 'JC').slice(0, 2).toUpperCase();
}
